import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import * as XLSX from 'xlsx';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { DesktopStudentDashboard, DesktopAnnouncementsView } from './DesktopComponents';
import pushNotificationService from './services/pushNotificationService';

// Configure axios defaults for better performance
axios.defaults.timeout = 8000; // 8 second timeout
axios.defaults.headers.post['Content-Type'] = 'application/json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initialize mobile app features
if (Capacitor.isNativePlatform()) {
  // Set status bar style
  StatusBar.setStyle({ style: Style.Default });
  StatusBar.setBackgroundColor({ color: '#7c3aed' });
  
  // Hide splash screen after app loads
  SplashScreen.hide();

  console.log("Capacitor platform detected");
  
  // Initialize push notifications
  pushNotificationService.initialize().then(result => {
    console.log("Push notification initialization:", result);
  });
  
  // Listen for push notifications received in foreground
  pushNotificationService.addListener('pushNotificationReceived', (notification) => {
    console.log("Push received in foreground:", notification);
    // You can show an in-app notification here
  });
  
  // Listen for notification taps
  pushNotificationService.addListener('pushNotificationActionPerformed', (action) => {
    console.log("User tapped notification:", action);
    // Navigate to relevant screen based on action.notification.data
  });
}

// Version Switcher Component
const VersionSwitcher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = location.pathname.startsWith('/desktop');
  
  // Don't show version switcher in mobile app (Capacitor) environment
  if (Capacitor.isNativePlatform()) {
    return null;
  }
  
  // Only show version switcher on desktop version, not on mobile web version
  if (!isDesktop) {
    return null;
  }
  
  const switchVersion = () => {
    navigate('/');
  };

  return (
    <button
      onClick={switchVersion}
      className="fixed top-4 right-4 z-50 bg-purple-600 hover:bg-purple-700 text-white shadow-lg rounded-full p-3 border-2 border-white transition-all duration-200 transform hover:scale-110"
      title="Switch to Mobile Version"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    </button>
  );
};

// Auth Context
const AuthContext = createContext();

// Notification Context
const NotificationContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Notification Provider
const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationSettings, setNotificationSettings] = useState({
    sound_enabled: true,
    vibration_enabled: true,
    push_enabled: true
  });
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const { user } = useAuth();

  // Sound effect for notifications
  const playNotificationSound = () => {
    if (notificationSettings.sound_enabled) {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAUBjqP1fPNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmA');
      audio.play().catch(e => console.log('Could not play notification sound'));
    }
  };

  // Vibration for notifications
  const triggerVibration = () => {
    if (notificationSettings.vibration_enabled) {
      if (Capacitor.isNativePlatform()) {
        // Use Capacitor Haptics for native apps
        Haptics.impact({ style: ImpactStyle.Medium });
      } else {
        // Use Web Vibration API for web
        if ('vibrate' in navigator) {
          navigator.vibrate([100, 50, 100]);
        }
      }
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/mark-read`);
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );
      fetchUnreadCount(); // Refresh unread count
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Send notification
  const sendNotification = async (notificationData) => {
    try {
      const response = await axios.post(`${API}/notifications/send`, notificationData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to send notification' };
    }
  };

  // Send fee reminder notification
  const sendFeeReminderNotification = async (announcementId, customMessage) => {
    try {
      const response = await axios.post(`${API}/notifications/send-fee-reminder`, {
        announcement_id: announcementId,
        custom_message: customMessage
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to send fee reminder' };
    }
  };

  // Update notification settings
  const updateNotificationSettings = async (settings) => {
    try {
      await axios.put(`${API}/notification-settings`, settings);
      setNotificationSettings(settings);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update settings' };
    }
  };

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    if (!user || user.is_instructor) return;
    
    try {
      const response = await axios.get(`${API}/notification-settings`);
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  };

  // Poll for new notifications
  useEffect(() => {
    if (!user) return;

    const pollInterval = setInterval(() => {
      fetchNotifications();
      fetchUnreadCount();
    }, 10000); // Poll every 10 seconds

    // Initial fetch
    fetchNotifications();
    fetchUnreadCount();
    fetchNotificationSettings();

    return () => clearInterval(pollInterval);
  }, [user]);

  // Show notification when new ones arrive
  useEffect(() => {
    if (notifications.length > 0) {
      const latestNotification = notifications[0];
      const now = new Date();
      const notificationTime = new Date(latestNotification.created_at);
      
      // If notification is less than 30 seconds old, show it
      if (now - notificationTime < 30000 && !latestNotification.is_read) {
        playNotificationSound();
        triggerVibration();
      }
    }
  }, [notifications]);

  const value = {
    notifications,
    unreadCount,
    notificationSettings,
    showNotificationPanel,
    setShowNotificationPanel,
    fetchNotifications,
    markAsRead,
    sendNotification,
    sendFeeReminderNotification,
    updateNotificationSettings,
    fetchNotificationSettings,
    playNotificationSound,
    triggerVibration
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize authentication on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Set the authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Verify token is still valid by calling profile endpoint
          const response = await axios.get(`${API}/profile`);
          setUser(response.data);
        } catch (error) {
          console.error('Authentication initialization failed:', error);
          // Token is invalid, remove it
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/login`, { email, password });
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch complete profile data instead of using limited login response
      await fetchProfile();
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/register`, userData);
      const { token, user: newUser } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(newUser);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Profile fetch failed:', error);
      logout();
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    fetchProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Notification Bell Component
const NotificationBell = () => {
  const { unreadCount, showNotificationPanel, setShowNotificationPanel } = useNotifications();

  return (
    <div className="relative">
      <button
        onClick={() => setShowNotificationPanel(!showNotificationPanel)}
        className="relative p-2 text-gray-400 hover:text-white transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};

// Notification Panel Component
const NotificationPanel = () => {
  const { 
    notifications, 
    showNotificationPanel, 
    setShowNotificationPanel, 
    markAsRead 
  } = useNotifications();

  if (!showNotificationPanel) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-96 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Notifications</h3>
          <button
            onClick={() => setShowNotificationPanel(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No notifications yet
            </div>
          ) : (
            notifications.map((notification) => (
              <div 
                key={notification.id} 
                className={`p-4 border-b border-gray-100 ${!notification.is_read ? 'bg-blue-50' : 'bg-white'}`}
                onClick={() => !notification.is_read && markAsRead(notification.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{notification.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      From: {notification.sender_name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-2"></div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Success Modal Component
const SuccessModal = ({ isOpen, title, message, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-6">{message}</p>
          <button
            onClick={onClose}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// SMS Setup Component
const SMSSetup = () => {
  const [smsStatus, setSmsStatus] = useState('checking');
  const [smsInfo, setSmsInfo] = useState(null);
  const [connectedUser, setConnectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const navigate = useNavigate();

  const checkStatus = async () => {
    try {
      setErrorDetails(null);
      const response = await axios.get(`${API}/sms/status`);
      const data = response.data;
      
      // Debug logging
      console.log('SMS Status API Response:', {
        status: data.status,
        connected: data.connected,
        user: data.user,
        instructorId: data.instructorId,
        phone_number: data.phone_number
      });
      
      // Check for service errors
      if (data.status === 'service_unavailable' || data.status === 'service_unreachable') {
        setSmsStatus('service_error');
        setErrorDetails({
          type: 'service_connectivity',
          message: 'SMS service is not accessible',
          error: data.error,
          phone_number: data.phone_number
        });
        setConnectedUser(null);
        return false;
      }
      
      // SMS is always ready if service is healthy
      if (data.connected === true || data.status === 'ready') {
        setSmsStatus('ready');
        setConnectedUser(data.user);
        setSmsInfo({
          phone_number: data.phone_number,
          service: data.service
        });
        console.log('SMS: Service is ready for sending messages');
      } else {
        setSmsStatus(data.status || 'disconnected');
        console.log('SMS: Setting status to:', data.status);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to check SMS status:', error);
      
      if (error.response?.status === 401) {
        setSmsStatus('service_error');
        setErrorDetails({
          type: 'authentication',
          message: 'Network error while checking SMS status',
          error: error.message
        });
        navigate('/login');
      }
      return false;
    }
  };

  const initializeSMS = async () => {
    try {
      console.log('SMS: Initializing service...');
      setLoading(true);
      const response = await axios.post(`${API}/sms/init`);
      console.log('SMS init response:', response.data);
      
      if (response.data.success) {
        // SMS service should be ready immediately
        await checkStatus();
        console.log('SMS: Service initialized successfully');
        alert('SMS service is ready for sending messages!');
      } else {
        console.error('SMS initialization failed:', response.data);
        setSmsStatus('service_error');
        setErrorDetails({
          type: 'initialization',
          message: response.data.message || 'Failed to initialize SMS service',
          error: response.data.error
        });
        alert(response.data.message || 'SMS service initialization failed');
      }
    } catch (error) {
      console.error('Failed to initialize SMS:', error);
      setSmsStatus('service_error');
      setErrorDetails({
        type: 'network',
        message: 'Network error during SMS initialization',
        error: error.message
      });
      alert('Network error during SMS initialization');
    } finally {
      setLoading(false);
    }
  };

  const getServiceInfo = async () => {
    try {
      console.log('SMS Setup: Fetching service info from API...');
      setLoading(true);
      const response = await axios.get(`${API}/sms/info`);
      const data = response.data;
      
      console.log('SMS Info API Response:', {
        service: data.service,
        phone_number: data.phone_number,
        status: data.status
      });
      
      if (data.service === 'twilio_sms') {
        setSmsInfo(data);
        setSmsStatus('ready');
        console.log('SMS Setup: Service info retrieved successfully');
        
        alert(`SMS service ready! Using phone number: ${data.phone_number}`);
      } else {
        setSmsStatus('service_error');
        setErrorDetails({
          type: 'service_unavailable',
          message: 'SMS service is not accessible',
          error: data.error
        });
        console.log('SMS Setup: Service info retrieval failed');
      }
    } catch (error) {
      console.error('Failed to get SMS service info:', error);
      setSmsStatus('service_error');
      setErrorDetails({
        type: 'network',
        message: 'Failed to connect to SMS service',
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-check status on mount
  useEffect(() => {
    console.log('SMS Setup: Component mounting, checking service status');
    checkStatus();
  }, []);

  // Auto-initialize if needed
  useEffect(() => {
    if (smsStatus === 'checking' || smsStatus === 'disconnected') {
      console.log('SMS Setup: Starting initialization...');
      
      const initTimer = setTimeout(() => {
        console.log('SMS Setup: Not ready, current status:', smsStatus);
        
        if (smsStatus !== 'ready' && smsStatus !== 'service_error') {
          console.log('SMS Setup: Auto-initializing SMS service...');
          initializeSMS();
        }
      }, 2000);
      
      return () => clearTimeout(initTimer);
    }
  }, [smsStatus]);

  const renderStatus = () => {
    switch (smsStatus) {
      case 'ready':
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-green-600">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="font-medium">SMS Service Ready</span>
            </div>
            {smsInfo && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">Service Information</h4>
                <div className="text-sm text-green-700 space-y-1">
                  <p><strong>SMS Number:</strong> {smsInfo.phone_number}</p>
                  <p><strong>Service:</strong> Twilio SMS</p>
                  <p><strong>Status:</strong> Ready to send messages</p>
                </div>
              </div>
            )}
            {connectedUser && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Connected As</h4>
                <p className="text-sm text-blue-700">{connectedUser.name}</p>
              </div>
            )}
          </div>
        );
      
      case 'checking':
        return (
          <div className="flex items-center space-x-2 text-blue-600">
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            <span>Checking SMS service status...</span>
          </div>
        );
      
      case 'service_error':
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-red-600">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="font-medium">SMS Service Error</span>
            </div>
            {errorDetails && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">Error Details</h4>
                <div className="text-sm text-red-700 space-y-1">
                  <p><strong>Type:</strong> {errorDetails.type}</p>
                  <p><strong>Message:</strong> {errorDetails.message}</p>
                  {errorDetails.error && <p><strong>Error:</strong> {errorDetails.error}</p>}
                </div>
              </div>
            )}
            <button
              onClick={initializeSMS}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Retrying...' : 'Retry SMS Setup'}
            </button>
          </div>
        );
      
      default:
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              <span>SMS service status: {smsStatus}</span>
            </div>
            <button
              onClick={getServiceInfo}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Connecting...' : 'Connect SMS Service'}
            </button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">📱 SMS Service Setup</h1>
          <p className="text-gray-600 mt-2">Set up SMS messaging to send notifications to students</p>
        </div>

        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
          {renderStatus()}
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">📱 How SMS Messaging Works</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Send individual messages to specific students</li>
              <li>• Send bulk messages to all students at once</li>
              <li>• Automatic fee reminders via SMS</li>
              <li>• No setup required - SMS is always ready to use</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notification Settings Component
const NotificationSettings = () => {
  const { 
    notificationSettings, 
    updateNotificationSettings,
    playNotificationSound,
    triggerVibration 
  } = useNotifications();
  
  const [localSettings, setLocalSettings] = useState(notificationSettings);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const result = await updateNotificationSettings(localSettings);
    setSaving(false);
    
    if (result.success) {
      alert('Settings saved successfully!');
    } else {
      alert('Failed to save settings: ' + result.error);
    }
  };

  const testSound = () => {
    playNotificationSound();
  };

  const testVibration = () => {
    triggerVibration();
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4">Notification Settings</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium">Sound Notifications</label>
            <p className="text-sm text-gray-600">Play sound when you receive notifications</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={testSound}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              Test
            </button>
            <input
              type="checkbox"
              checked={localSettings.sound_enabled}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, sound_enabled: e.target.checked }))}
              className="rounded"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium">Vibration</label>
            <p className="text-sm text-gray-600">Vibrate when you receive notifications</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={testVibration}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              Test
            </button>
            <input
              type="checkbox"
              checked={localSettings.vibration_enabled}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, vibration_enabled: e.target.checked }))}
              className="rounded"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium">Push Notifications</label>
            <p className="text-sm text-gray-600">Receive notifications when app is closed</p>
          </div>
          <input
            type="checkbox"
            checked={localSettings.push_enabled}
            onChange={(e) => setLocalSettings(prev => ({ ...prev, push_enabled: e.target.checked }))}
            className="rounded"
          />
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

// Sensei Setup Modal Component
const SenseiSetupModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact_area_code: '',
    contact_number: '',
    password: '',
    retype_password: '',
    city: '',
    state: '',
    dojo_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/\d/.test(password)) return "Password must contain at least one digit";
    return null;
  };

  const validateEmail = (email) => {
    if (!email.toLowerCase().endsWith('.com')) return "Email must have .com domain";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Client-side validation
      const emailError = validateEmail(formData.email);
      if (emailError) {
        setError(emailError);
        setLoading(false);
        return;
      }

      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        setError(passwordError);
        setLoading(false);
        return;
      }

      if (formData.password !== formData.retype_password) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      // Validate contact number format
      if (!/^\d{3}$/.test(formData.contact_area_code)) {
        setError('Area code must be 3 digits');
        setLoading(false);
        return;
      }

      if (!/^\d{7}$/.test(formData.contact_number)) {
        setError('Phone number must be 7 digits');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/sensei/register`, formData);

      if (response.status === 200) {
        setSuccess(true);
        // Reset form
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          contact_area_code: '',
          contact_number: '',
          password: '',
          retype_password: '',
          city: '',
          state: ''
        });
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create account');
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle specific input formatting
    if (name === 'contact_area_code' || name === 'contact_number') {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, '');
      
      if (name === 'contact_area_code' && digitsOnly.length <= 3) {
        setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      } else if (name === 'contact_number' && digitsOnly.length <= 7) {
        setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      }
    } else if (name === 'first_name' || name === 'last_name' || name === 'city') {
      // Only allow letters and spaces
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, '');
      setFormData(prev => ({ ...prev, [name]: lettersOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      contact_area_code: '',
      contact_number: '',
      password: '',
      retype_password: '',
      city: '',
      state: '',
      dojo_name: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold z-10"
          >
            ×
          </button>
          
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-center">
            <h1 className="text-3xl font-bold text-white mb-2">🎉 Success!</h1>
            <p className="text-green-100">Account Created</p>
          </div>

          <div className="p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sensei Account Created!</h3>
              <p className="text-sm text-gray-600">
                Your instructor account has been successfully created. Our Admin team will reach out to you to setup your professional account. Once your account is activated then you can start using our website to manage your Dojo.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md transition-colors"
            >
              Got it, Thanks!
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold z-10"
        >
          ×
        </button>

        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">🥋 New Sensei Setup</h1>
          <p className="text-purple-100">Create Your Instructor Account</p>
        </div>

        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="example@domain.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Must be a .com email address</p>
            </div>

            {/* Dojo Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dojo Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="dojo_name"
                value={formData.dojo_name}
                onChange={handleInputChange}
                placeholder="e.g., Austin Shotokan Karate Academy"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This will be displayed in your dashboard header</p>
            </div>

            {/* Contact Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  name="contact_area_code"
                  value={formData.contact_area_code}
                  onChange={handleInputChange}
                  placeholder="123"
                  maxLength="3"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center"
                  required
                />
                <input
                  type="text"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  placeholder="4567890"
                  maxLength="7"
                  className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">3 digit area code + 7 digit number</p>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">8+ chars, 1 uppercase, 1 digit</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retype Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="retype_password"
                  value={formData.retype_password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* City and State */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
              >
                {loading ? 'Creating Account...' : 'Create Sensei Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// System Configuration Panel Component
const SystemConfigurationPanel = () => {
  const [systemConfig, setSystemConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newDailyLimit, setNewDailyLimit] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    fetchSystemConfig();
  }, []);

  const fetchSystemConfig = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/system-config`);
      setSystemConfig(response.data);
      setNewDailyLimit(response.data.daily_message_limit.toString());
    } catch (error) {
      console.error('Error fetching system config:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateDailyLimit = async () => {
    if (!newDailyLimit || parseInt(newDailyLimit) < 1 || parseInt(newDailyLimit) > 10000) {
      alert('Daily limit must be between 1 and 10,000');
      return;
    }

    setUpdating(true);
    try {
      const response = await axios.put(`${API}/super-admin/daily-message-limit`, {
        daily_limit: parseInt(newDailyLimit)
      });
      
      if (response.data.success) {
        await fetchSystemConfig();
        setShowEditForm(false);
        alert(`Daily message limit updated to ${newDailyLimit} successfully!`);
      }
    } catch (error) {
      console.error('Error updating daily limit:', error);
      alert('Failed to update daily message limit');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Loading system configuration...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Daily Message Limit Configuration */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-lg">📊</span>
              </div>
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-medium text-gray-900">Daily Message Limit</h4>
              <p className="text-sm text-gray-600">
                Maximum SMS messages per instructor per day
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {!showEditForm ? (
              <>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">
                    {systemConfig?.daily_message_limit || 0}
                  </div>
                  <div className="text-xs text-gray-500">messages/day</div>
                </div>
                <button
                  onClick={() => setShowEditForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Edit Limit
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={newDailyLimit}
                  onChange={(e) => setNewDailyLimit(e.target.value)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="300"
                />
                <button
                  onClick={updateDailyLimit}
                  disabled={updating}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  {updating ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setShowEditForm(false);
                    setNewDailyLimit(systemConfig?.daily_message_limit.toString() || '300');
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
        
        {showEditForm && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
            <div className="text-sm text-gray-600 space-y-1">
              <p>• <strong>Current Limit:</strong> {systemConfig?.daily_message_limit || 0} messages per day</p>
              <p>• <strong>Valid Range:</strong> 1 to 10,000 messages</p>
              <p>• <strong>Effect:</strong> Applies to all instructors immediately</p>
              <p>• <strong>Reset:</strong> Daily limits reset at midnight UTC</p>
            </div>
          </div>
        )}
      </div>

      {/* Future Configuration Options Placeholder */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">🔧</span>
            </div>
          </div>
          <div className="ml-4">
            <h4 className="text-lg font-medium text-gray-500">Additional System Settings</h4>
            <p className="text-sm text-gray-400">
              More configuration options will be available in future updates
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Super Admin Dashboard Component
const SuperAdminDashboard = () => {
  const [senseiData, setSenseiData] = useState([]);
  const [filteredSenseiData, setFilteredSenseiData] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'inactive'
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    // Apply filter whenever senseiData or activeFilter changes
    applyFilter();
  }, [senseiData, activeFilter]);

  const applyFilter = () => {
    if (activeFilter === 'all') {
      setFilteredSenseiData(senseiData);
    } else if (activeFilter === 'active') {
      setFilteredSenseiData(senseiData.filter(sensei => sensei.is_active));
    } else if (activeFilter === 'inactive') {
      setFilteredSenseiData(senseiData.filter(sensei => !sensei.is_active));
    }
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch sensei list
      const senseiResponse = await axios.get(`${API}/super-admin/sensei`, { headers });
      setSenseiData(senseiResponse.data.sensei_list || []);
      
      // Fetch dashboard stats
      const statsResponse = await axios.get(`${API}/super-admin/dashboard-stats`, { headers });
      setDashboardStats(statsResponse.data);
      
    } catch (error) {
      console.error('Error fetching super admin data:', error);
      setError('Failed to load dashboard data');
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalMessage, setStatusModalMessage] = useState('');
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const toggleSenseiStatus = async (senseiId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.put(
        `${API}/super-admin/sensei/${senseiId}/toggle-active`,
        {},
        { headers }
      );
      
      if (response.status === 200) {
        // Update local state
        setSenseiData(prevData =>
          prevData.map(sensei =>
            sensei.sensei_id === senseiId
              ? { ...sensei, is_active: response.data.is_active }
              : sensei
          )
        );
        
        // Update dashboard stats
        fetchDashboardData();
        
        const action = response.data.is_active ? 'activated' : 'deactivated';
        setStatusModalMessage(`Sensei account ${action} successfully!`);
        setShowStatusModal(true);
      }
    } catch (error) {
      console.error('Error toggling sensei status:', error);
      setStatusModalMessage('Failed to update sensei status. Please try again.');
      setShowStatusModal(true);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetTarget || !newPassword) {
      setStatusModalMessage('Please enter a new password');
      setShowStatusModal(true);
      return;
    }

    setResetLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.put(
        `${API}/super-admin/sensei/${resetTarget.sensei_id}/reset-password`,
        {
          new_password: newPassword
        },
        { headers }
      );
      
      if (response.status === 200) {
        setShowPasswordResetModal(false);
        setNewPassword('');
        setResetTarget(null);
        setStatusModalMessage(`✅ Password reset successful for ${resetTarget.full_name}! 
        
📧 Email notification sent to: ${resetTarget.email}
🔑 New password: ${newPassword}

The instructor has been notified with login instructions.`);
        setShowStatusModal(true);
        
        // Refresh data to show any updates
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      setStatusModalMessage(`Password reset failed: ${errorMessage}`);
      setShowStatusModal(true);
    } finally {
      setResetLoading(false);
    }
  };

  const openPasswordResetModal = (sensei) => {
    setResetTarget(sensei);
    setNewPassword('');
    setShowPasswordResetModal(true);
  };

  const handleLogout = () => {
    logout();
    // Use window.location.href to bypass React Router's ProtectedRoute redirect
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading Super Admin Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">🛡️ Super Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Sensei Suite Admin Panel</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFilterClick('all')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="text-2xl">👨‍🏫</span>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Sensei</dt>
                    <dd className="text-lg font-medium text-gray-900">{dashboardStats.total_sensei || 0}</dd>
                  </dl>
                </div>
              </div>
              {activeFilter === 'all' && (
                <div className="mt-2 text-xs text-blue-600 font-medium">
                  📍 Currently showing all sensei
                </div>
              )}
            </div>
          </div>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFilterClick('active')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="text-2xl">✅</span>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Sensei</dt>
                    <dd className="text-lg font-medium text-green-600">{dashboardStats.active_sensei || 0}</dd>
                  </dl>
                </div>
              </div>
              {activeFilter === 'active' && (
                <div className="mt-2 text-xs text-green-600 font-medium">
                  📍 Currently filtering active sensei
                </div>
              )}
            </div>
          </div>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFilterClick('inactive')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="text-2xl">❌</span>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Inactive Sensei</dt>
                    <dd className="text-lg font-medium text-red-600">{dashboardStats.inactive_sensei || 0}</dd>
                  </dl>
                </div>
              </div>
              {activeFilter === 'inactive' && (
                <div className="mt-2 text-xs text-red-600 font-medium">
                  📍 Currently filtering inactive sensei
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sensei Management Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Sensei Management ({filteredSenseiData.length} {activeFilter === 'all' ? 'total' : activeFilter})
                </h3>
                {activeFilter !== 'all' && (
                  <button
                    onClick={() => handleFilterClick('all')}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Show All Sensei
                  </button>
                )}
              </div>
              <button
                onClick={fetchDashboardData}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Refresh Data
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sensei ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dojo Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Students
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSenseiData.map((sensei) => (
                    <tr key={sensei.sensei_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {sensei.sensei_id}
                        <div className="text-xs text-gray-500">
                          Joined: {new Date(sensei.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {sensei.full_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-purple-600">
                          {sensei.dojo_name || 'Not Set'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {sensei.dojo_name ? 'Custom Branding' : 'Default Branding'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {sensei.contact_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {sensei.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {sensei.city}, {sensei.state}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {sensei.total_students || 0} students
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            sensei.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {sensei.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => toggleSenseiStatus(sensei.sensei_id, sensei.is_active)}
                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md ${
                              sensei.is_active
                                ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                : 'text-green-700 bg-green-100 hover:bg-green-200'
                            }`}
                          >
                            {sensei.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          
                          <button
                            onClick={() => openPasswordResetModal(sensei)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                            title="Reset Password"
                          >
                            🔑 Reset
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredSenseiData.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No {activeFilter === 'all' ? '' : activeFilter} sensei accounts found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 rounded-full p-3 mr-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Status Update</h3>
              </div>
              
              <p className="text-gray-600 mb-6">{statusModalMessage}</p>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Configuration Section */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">⚙️ System Configuration</h3>
          <p className="text-sm text-gray-600">Manage system-wide settings and limits</p>
        </div>
        <div className="p-6">
          <SystemConfigurationPanel />
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-100 rounded-full p-3 mr-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2l-2.257-2.257A6 6 0 0112 5zm-6 4a2 2 0 112 2 2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">🔐 Admin Password Reset</h3>
                  <p className="text-xs text-gray-600">Super Admin Action - Audit Logged</p>
                </div>
              </div>
              
              {resetTarget && (
                <>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-yellow-800">⚠️ Security Alert</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          You are about to reset the password for <strong>{resetTarget.full_name}</strong> ({resetTarget.email}). This action will:
                        </p>
                        <ul className="list-disc list-inside text-xs text-yellow-700 mt-2 space-y-1">
                          <li>Immediately invalidate their current password</li>
                          <li>Send email notification with new password & login instructions</li>
                          <li>Log this admin action for security audit</li>
                          <li>Require instructor to change password on first login (recommended)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">👤 Instructor Information:</h4>
                    <div className="space-y-1 text-sm">
                      <p><strong>Name:</strong> {resetTarget.full_name}</p>
                      <p><strong>Email:</strong> {resetTarget.email}</p>
                      <p><strong>Dojo:</strong> {resetTarget.dojo_name || 'Legacy Instructor'}</p>
                      <p><strong>Status:</strong> <span className={resetTarget.is_active ? 'text-green-600' : 'text-red-600'}>{resetTarget.is_active ? 'Active' : 'Inactive'}</span></p>
                    </div>
                  </div>
                </>
              )}
              
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    New Password *
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      // Generate a secure password
                      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
                      let password = '';
                      // Ensure at least 1 uppercase, 1 lowercase, 1 digit, 1 special char
                      password += 'ABCDEFGHJKLMNPQRSTUVWXYZ'[Math.floor(Math.random() * 25)];
                      password += 'abcdefghijkmnpqrstuvwxyz'[Math.floor(Math.random() * 26)];
                      password += '23456789'[Math.floor(Math.random() * 8)];
                      password += '!@#$%^&*'[Math.floor(Math.random() * 8)];
                      // Add 8 more random characters
                      for (let i = 0; i < 8; i++) {
                        password += chars[Math.floor(Math.random() * chars.length)];
                      }
                      // Shuffle the password
                      setNewPassword(password.split('').sort(() => 0.5 - Math.random()).join(''));
                    }}
                    className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-md transition-colors"
                  >
                    🎲 Generate Secure
                  </button>
                </div>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono text-sm"
                  placeholder="Enter new permanent password"
                  required
                  minLength="8"
                />
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  <p>✅ <strong>Requirements:</strong> Minimum 8 characters, 1 uppercase, 1 digit, 1 special character</p>
                  <p>🔒 <strong>Security:</strong> This will be a permanent password (not temporary)</p>
                  <p>📧 <strong>Notification:</strong> Instructor will receive email with login instructions</p>
                </div>
                
                {/* Password strength indicator */}
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center text-xs">
                      <span className="text-gray-600 mr-2">Strength:</span>
                      {newPassword.length >= 8 && 
                       /[A-Z]/.test(newPassword) && 
                       /[0-9]/.test(newPassword) && 
                       /[^A-Za-z0-9]/.test(newPassword) ? (
                        <span className="text-green-600 font-medium">✅ Strong</span>
                      ) : newPassword.length >= 8 ? (
                        <span className="text-yellow-600 font-medium">⚠️ Moderate</span>
                      ) : (
                        <span className="text-red-600 font-medium">❌ Weak</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-blue-800 mb-2">📋 What happens next:</h4>
                <ol className="list-decimal list-inside text-xs text-blue-700 space-y-1">
                  <li>Password will be updated immediately in the database</li>
                  <li>Automated email sent to instructor with new password & instructions</li>
                  <li>Admin action logged with timestamp and your admin ID</li>
                  <li>Instructor must use new password for next login</li>
                  <li>Recommend instructor changes password after first login</li>
                </ol>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setNewPassword('');
                    setResetTarget(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  disabled={resetLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`⚠️ CONFIRM PASSWORD RESET\n\nYou are about to reset the password for:\n${resetTarget?.full_name} (${resetTarget?.email})\n\nThis action cannot be undone and will be logged for security audit.\n\nContinue with password reset?`)) {
                      handlePasswordReset();
                    }
                  }}
                  disabled={resetLoading || !newPassword || newPassword.length < 8}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {resetLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Resetting Password...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2l-2.257-2.257A6 6 0 0112 5zm-6 4a2 2 0 112 2 2 2 0 01-2-2z" />
                      </svg>
                      <span>🔐 Reset Password</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Login Modal Component  
const LoginModal = ({ isOpen, onClose, onOpenSenseiSetup }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      onClose(); // Close modal first
      
      // Check if user is super admin
      if (result.user.is_super_admin) {
        navigate('/super-admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleNewInstructorSetup = () => {
    onOpenSenseiSetup();
  };

  const handleForgotPassword = () => {
    // Placeholder for Forgot Password functionality  
    alert('Forgot Password - Feature coming soon!');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold z-10"
        >
          ×
        </button>

        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">🥋 Sensei Suite</h1>
          <p className="text-purple-100">Martial Arts Management</p>
        </div>

        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your email in lowercase"
                required
              />
              <p className="text-xs text-amber-600 mt-1 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Please enter your email address in all lowercase letters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <button
              onClick={handleNewInstructorSetup}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium underline block w-full"
            >
              New Instructor Setup
            </button>
            <button
              onClick={handleForgotPassword}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium underline block w-full"
            >
              Forgot Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Component (keep for direct /login route access)
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // Check if user is super admin
      if (result.user.is_super_admin) {
        navigate('/super-admin', { replace: true });
      } else {
        // Preserve the path they were trying to access
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">🥋 Sensei Suite</h1>
          <p className="text-purple-100">Martial Arts Management</p>
        </div>

        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your email in lowercase"
                required
              />
              <p className="text-xs text-amber-600 mt-1 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Please enter your email address in all lowercase letters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Add Student Modal Component (for instructors)
const AddStudentModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    belt_level: 'white',
    date_of_birth: '',  // Changed from age to date_of_birth
    gender: 'male',
    phone_number: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    class_preferences: [],
    enrolled_classes: [],  // NEW: For proper class enrollment
    is_trial: false,
    monthly_payment_amount: 0,
    app_returned: false,  // NEW: APP Retrnd field
    payment_method: '',   // NEW: Payment Method
    current_month_paid: false  // NEW: Current month paid status
  });
  const [availableClasses, setAvailableClasses] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [studentName, setStudentName] = useState('');

  // Fetch available classes when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailableClasses();
    }
  }, [isOpen]);

  const fetchAvailableClasses = async () => {
    try {
      setLoadingClasses(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableClasses(response.data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError('Failed to load available classes');
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation: Check if at least one class is selected
    if (formData.enrolled_classes.length === 0) {
      setError('Student must be enrolled in at least one class.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      
      // Create student first
      const response = await axios.post(`${API}/students`, {
        ...formData
        // enrolled_classes will be handled via separate enrollment endpoints
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const studentId = response.data.student?.id;
      
      // Enroll student in selected classes
      if (studentId && formData.enrolled_classes.length > 0) {
        for (const classId of formData.enrolled_classes) {
          try {
            await axios.post(`${API}/students/${studentId}/enroll/${classId}`, {}, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
          } catch (enrollError) {
            console.warn(`Failed to enroll in class ${classId}:`, enrollError);
          }
        }
      }
      
      setStudentName(formData.name);
      setShowSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        password: '',
        belt_level: 'white',
        date_of_birth: '',  // Changed from age to date_of_birth
        gender: 'male',
        phone_number: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        class_preferences: [],
        enrolled_classes: [],
        is_trial: false,
        monthly_payment_amount: 0
      });
    } catch (error) {
      console.error('Student creation error:', error);
      
      if (error.response) {
        // Server responded with error
        const errorMessage = error.response.data?.detail || 
                           error.response.data?.message || 
                           error.response.data?.error || 
                           `Server error: ${error.response.status}`;
        setError(errorMessage);
      } else if (error.request) {
        // Request was made but no response received
        setError('Network error: Unable to connect to server');
      } else {
        // Something else happened
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onSuccess(); // This will refresh the data and close the modal
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validation rules
    if (name === 'name') {
      // Only letters and spaces allowed for name
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, '');
      setFormData(prev => ({ ...prev, [name]: lettersOnly }));
    } else if (name === 'phone_number') {
      // Only numbers allowed for phone number (plus sign allowed at start)
      const numbersOnly = value.replace(/[^0-9+\-\s()]/g, '');
      setFormData(prev => ({ ...prev, [name]: numbersOnly }));
    } else if (name === 'date_of_birth') {
      // Date of birth - no special validation needed as HTML date input handles format
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === 'emergency_contact_name') {
      // Only letters and spaces allowed for emergency contact name
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, '');
      setFormData(prev => ({ ...prev, [name]: lettersOnly }));
    } else if (name === 'emergency_contact_phone') {
      // Only numbers allowed for emergency contact phone
      const numbersOnly = value.replace(/[^0-9+\-\s()]/g, '');
      setFormData(prev => ({ ...prev, [name]: numbersOnly }));
    } else if (name === 'monthly_payment_amount') {
      // Only numbers and decimal point allowed for payment amount
      const numbersOnly = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numbersOnly.split('.');
      const sanitizedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numbersOnly;
      setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleClassPreferenceChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      class_preferences: checked
        ? [...prev.class_preferences, value]
        : prev.class_preferences.filter(pref => pref !== value)
    }));
  };

  const handleClassEnrollmentChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      enrolled_classes: checked
        ? [...prev.enrolled_classes, value]
        : prev.enrolled_classes.filter(classId => classId !== value)
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-md max-h-96 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Enroll New Student</h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="student@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initial Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter initial password"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]} // Prevent future dates
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Belt Level</label>
                  <select
                    name="belt_level"
                    value={formData.belt_level}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="white">White</option>
                    <option value="yellow">Yellow</option>
                    <option value="orange">Orange</option>
                    <option value="green">Green</option>
                    <option value="blue">Blue</option>
                    <option value="brown">Brown</option>
                    <option value="black">Black</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Payment Amount ($) *</label>
                <input
                  type="number"
                  name="monthly_payment_amount"
                  value={formData.monthly_payment_amount}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name *</label>
                <input
                  type="text"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone *</label>
                <input
                  type="tel"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class Enrollment * 
                  <span className="text-xs text-gray-500 ml-1">(Select at least one)</span>
                </label>
                {loadingClasses ? (
                  <div className="p-3 border border-gray-300 rounded-md">
                    <p className="text-sm text-gray-500">Loading available classes...</p>
                  </div>
                ) : availableClasses.length === 0 ? (
                  <div className="p-3 border border-gray-300 rounded-md">
                    <p className="text-sm text-gray-500">No classes available. Please create classes in Class Center first.</p>
                  </div>
                ) : (
                  <div className={`space-y-2 p-3 border rounded-md ${
                    formData.enrolled_classes.length === 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}>
                    {availableClasses.map(classItem => (
                      <label key={classItem.id} className="flex items-center">
                        <input
                          type="checkbox"
                          value={classItem.id}
                          checked={formData.enrolled_classes.includes(classItem.id)}
                          onChange={handleClassEnrollmentChange}
                          className="mr-2"
                        />
                        <div className="text-sm">
                          <div className="font-medium">{classItem.name}</div>
                          <div className="text-gray-600 text-xs">{classItem.time} - {classItem.location}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {formData.enrolled_classes.length === 0 && (
                  <p className="text-xs text-red-600 mt-1">Please select at least one class</p>
                )}
              </div>

              {/* Trial Student Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trial Student</label>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_trial: !prev.is_trial }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                      formData.is_trial ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        formData.is_trial ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {formData.is_trial ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  {loading ? 'Adding Student...' : 'Add Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccess}
        title="Student Added Successfully!"
        message={`${studentName} has been added to the dojo. You can now manage their profile and mark their attendance.`}
        onClose={handleSuccessClose}
      />
    </>
  );
};

// Main Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  if (!user) return null;

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    // Use window.location.href to bypass React Router's ProtectedRoute redirect
    window.location.href = '/';
  };

  // Build the list of nav items (varies for instructor vs student)
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'home', show: true, activeCheck: () => isActive('/dashboard') },
    { path: '/students', label: 'Students', icon: 'users', show: !!user.is_instructor, activeCheck: () => isActive('/students') || isActive('/attendance') },
    { path: '/class-center', label: 'Classes', icon: 'building', show: !!user.is_instructor, activeCheck: () => isActive('/class-center') },
    { path: '/payment-center', label: 'Payments', icon: 'dollar', show: !!user.is_instructor, activeCheck: () => isActive('/payment-center') },
    { path: '/announcements', label: 'Messages', icon: 'chat', show: true, activeCheck: () => isActive('/announcements') },
  ].filter((item) => item.show);

  // Full labels for desktop (kept unchanged from before)
  const desktopNavItems = [
    { path: '/dashboard', label: 'Dashboard', show: true, activeCheck: () => isActive('/dashboard') },
    { path: '/students', label: 'Student Center', show: !!user.is_instructor, activeCheck: () => isActive('/students') || isActive('/attendance') },
    { path: '/class-center', label: 'Class Center', show: !!user.is_instructor, activeCheck: () => isActive('/class-center') },
    { path: '/payment-center', label: 'Payment Center', show: !!user.is_instructor, activeCheck: () => isActive('/payment-center') },
    { path: '/announcements', label: 'Message Center', show: true, activeCheck: () => isActive('/announcements') },
  ].filter((item) => item.show);

  const NavIcon = ({ name, className }) => {
    const paths = {
      home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
      users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      building: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      dollar: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    };
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={paths[name]} />
      </svg>
    );
  };

  return (
    <>
    <nav className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14 md:h-16">
          <div className="flex items-center md:space-x-8 space-x-3 flex-1 min-w-0">
            <div className="flex items-center shrink-0">
              <span className="text-lg md:text-xl font-bold">🥋<span className="hidden sm:inline ml-1">Sensei Suite</span></span>
            </div>

            {/* Desktop: horizontal tabs */}
            <div className="hidden md:flex space-x-4">
              {desktopNavItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  data-testid={`nav-tab-${item.path.replace('/', '')}`}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    item.activeCheck() ? 'bg-white bg-opacity-20' : 'hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-1 md:space-x-4 shrink-0">
            <NotificationBell />
            {!user.is_instructor && (
              <button
                onClick={() => handleNavigation('/notification-settings')}
                className="px-3 py-2 rounded-md text-sm font-medium hover:bg-white hover:bg-opacity-10"
                data-testid="settings-btn"
              >
                Settings
              </button>
            )}
            <button
              onClick={() => user.is_instructor ? setShowSettingsModal(true) : null}
              className={`hidden md:inline-flex text-sm ${user.is_instructor ? 'hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md cursor-pointer transition-colors' : 'cursor-default'}`}
              disabled={!user.is_instructor}
              data-testid="user-name-btn"
            >
              {user.name} {user.is_instructor ? '(Instructor)' : '(Student)'}
            </button>
            {/* Mobile: instructor settings gear */}
            {user.is_instructor && (
              <button
                onClick={() => setShowSettingsModal(true)}
                className="md:hidden p-2 rounded-md hover:bg-white hover:bg-opacity-10"
                data-testid="mobile-settings-gear"
                aria-label="Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1.5 md:py-2 rounded-md text-xs md:text-sm font-medium"
              data-testid="logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      
      {/* Instructor Settings Modal */}
      {showSettingsModal && (
        <InstructorSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          user={user}
        />
      )}
    </nav>

    {/* Mobile Bottom Tab Bar — thumb-reachable native nav */}
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-5">
        {navItems.map((item) => {
          const active = item.activeCheck();
          return (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              data-testid={`bottom-nav-${item.path.replace('/', '')}`}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
                active ? 'text-purple-600' : 'text-gray-500 hover:text-purple-500'
              }`}
              aria-current={active ? 'page' : undefined}
            >
              <div className={`relative flex items-center justify-center w-6 h-6 ${active ? '' : ''}`}>
                <NavIcon name={item.icon} className={`w-6 h-6 ${active ? 'stroke-[2.5]' : ''}`} />
              </div>
              <span className={`text-[10px] leading-tight ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
    </>
  );
};

// Instructor Settings Modal Component
const InstructorSettingsModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    confirmPassword: '',
    dojo_name: user?.dojo_name || '',
    phone_number: user?.contact_number || '',
    city: user?.city || '',
    state: user?.state || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { fetchProfile, logout } = useAuth();

  // Reset form when modal opens and fetch latest profile data
  useEffect(() => {
    if (isOpen && user) {
      // Fetch the latest profile data when modal opens
      const loadProfile = async () => {
        try {
          await fetchProfile();
        } catch (error) {
          console.error('Failed to fetch profile:', error);
        }
      };
      
      loadProfile();
      
      setError('');
      setSuccess('');
    }
  }, [isOpen, fetchProfile, user]);

  // Update form data when user data changes
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        dojo_name: user.dojo_name || '',
        phone_number: user.contact_number || '',
        city: user.city || '',
        state: user.state || ''
      });
    }
  }, [isOpen, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords if provided
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password && formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      // Prepare update data (exclude name and confirmPassword, don't include empty password)
      const updateData = {
        // name: formData.name, // Remove name from update since it's display-only
        email: formData.email,
        dojo_name: formData.dojo_name,
        phone_number: formData.phone_number,
        city: formData.city,
        state: formData.state
      };

      // Only include password if provided
      if (formData.password.trim()) {
        updateData.password = formData.password;
      }

      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/profile`, updateData);

      if (response.status === 200) {
        // Show logout confirmation modal instead of success message
        setShowLogoutModal(true);
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      setError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout confirmation
  const handleLogoutConfirm = () => {
    logout();
    window.location.href = '/';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">⚙️ Instructor Settings</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Instructor Name - Display Only */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructor Name
              </label>
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600">
                {user?.name || 'Not available'}
              </div>
            </div>

            {/* Instructor Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructor Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                placeholder="Enter your email address"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password (leave blank to keep current)
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  placeholder="Enter new password (min 8 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? '👁️' : '🙈'}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            {formData.password && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password *
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  placeholder="Confirm your new password"
                  required
                />
              </div>
            )}

            {/* Dojo Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dojo Name *
              </label>
              <input
                type="text"
                name="dojo_name"
                value={formData.dojo_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                placeholder="Enter your dojo name"
                required
              />
            </div>

            {/* Contact Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number
              </label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number || ''}
                onChange={(e) => {
                  const { name, value } = e.target;
                  setFormData(prev => ({
                    ...prev,
                    [name]: value
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                placeholder="Enter your phone number"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                placeholder="Enter your city"
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                placeholder="Enter your state"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Update Profile</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Updated Successfully!</h3>
              <p className="text-gray-600 mb-6">Profile has been updated, due to security reasons, please login again</p>
              <button 
                onClick={handleLogoutConfirm}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      if (user.is_instructor) {
        const [statsRes, attendanceRes, announcementsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/attendance`),
          axios.get(`${API}/announcements`)
        ]);
        setStats(statsRes.data);
        setAttendance(attendanceRes.data.slice(0, 10));
        setAnnouncements(announcementsRes.data.slice(0, 5));
      } else {
        const [attendanceRes, announcementsRes] = await Promise.all([
          axios.get(`${API}/attendance`),
          axios.get(`${API}/announcements`)
        ]);
        setAttendance(attendanceRes.data.slice(0, 10));
        setAnnouncements(announcementsRes.data.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        <div className="mb-4 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Welcome back, {user.name}! 👋
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">
            {user.is_instructor ? 
              (user.dojo_name ? `${user.dojo_name} Dashboard` : 'Instructor Dashboard') 
              : 'Student Dashboard'
            }
          </p>
        </div>

        {user.is_instructor ? (
          <InstructorDashboard 
            stats={stats} 
            attendance={attendance} 
            announcements={announcements}
            onRefreshData={fetchDashboardData}
          />
        ) : (
          <StudentDashboard attendance={attendance} announcements={announcements} />
        )}
      </div>
      
      <NotificationPanel />
    </div>
  );
};

// Birthday Calendar Component
const BirthdayCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchBirthdays();
  }, []);

  const fetchBirthdays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/student-birthdays`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBirthdays(response.data);
    } catch (error) {
      console.error('Failed to fetch birthdays:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDate = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < startDate; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getBirthdaysForDate = (day) => {
    if (!day) return [];
    
    const dateStr = `${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return birthdays.filter(birthday => {
      const birthDate = birthday.date_of_birth;
      if (!birthDate) return false;
      const birthMonthDay = birthDate.slice(5); // Get MM-DD from YYYY-MM-DD
      return birthMonthDay === dateStr;
    });
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', { 
      month: 'long', 
      year: 'numeric' 
    }).format(date);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const updateBirthdayDetailsPanel = (day, dayBirthdays) => {
    const detailsPanel = document.getElementById('birthday-details-panel');
    if (detailsPanel && dayBirthdays.length > 0) {
      const monthName = currentDate.toLocaleDateString('en-US', { month: 'long' });
      detailsPanel.innerHTML = `
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Birthday Details</h3>
        <div class="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <h4 class="text-md font-medium text-purple-800 mb-3">
            🎉 Birthday on ${monthName} ${day}
          </h4>
          <div class="space-y-2">
            ${dayBirthdays.map(birthday => `
              <div class="text-sm text-purple-700 bg-white rounded p-2 border border-purple-100">
                🎂 <strong>${birthday.student_name}</strong>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    } else if (detailsPanel) {
      detailsPanel.innerHTML = `
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Birthday Details</h3>
        <div class="text-center text-gray-500">
          <div class="mb-4">
            <span class="text-4xl">🎂</span>
          </div>
          <p>Click on a calendar date to view birthday details</p>
        </div>
      `;
    }
  };

  const days = getDaysInMonth(currentDate);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🎂 Student Birthdays</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3">Loading calendar...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">🎂 Student Birthdays</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={goToPreviousMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h4 className="text-md font-medium text-gray-700 min-w-[120px] text-center">
            {formatDate(currentDate)}
          </h4>
          <button
            onClick={goToNextMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          const dayBirthdays = getBirthdaysForDate(day);
          const hasBirthday = dayBirthdays.length > 0;
          
          // Check if this is the current date
          const today = new Date();
          const isToday = day && 
                         currentDate.getFullYear() === today.getFullYear() && 
                         currentDate.getMonth() === today.getMonth() && 
                         day === today.getDate();
          
          return (
            <div key={index} className="relative">
              {day ? (
                <button
                  onClick={() => {
                    if (hasBirthday) {
                      setSelectedDate({ day, birthdays: dayBirthdays });
                      updateBirthdayDetailsPanel(day, dayBirthdays);
                    } else {
                      setSelectedDate(null);
                      updateBirthdayDetailsPanel(null, []);
                    }
                  }}
                  className={`w-full h-8 text-sm rounded hover:bg-gray-100 ${
                    isToday 
                      ? 'bg-purple-600 text-white font-bold border-2 border-purple-800' 
                      : hasBirthday 
                        ? 'bg-purple-100 text-purple-800 font-medium border border-purple-300' 
                        : 'text-gray-700'
                  }`}
                >
                  {day}
                  {hasBirthday && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-[8px] text-white font-bold">{dayBirthdays.length}</span>
                    </div>
                  )}
                </button>
              ) : (
                <div className="w-full h-8"></div>
              )}
            </div>
          );
        })}
      </div>

      {birthdays.length === 0 && (
        <div className="mt-4 text-center text-gray-500 text-sm">
          No student birthdays recorded yet
        </div>
      )}
    </div>
  );
};

// Class Management Component
const ClassManagement = ({ stats, onRefreshData }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    time: '',
    location: ''
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = () => {
    setFormData({ name: '', description: '', time: '', location: '' });
    setShowAddModal(true);
  };

  const handleEditClass = (classData) => {
    setSelectedClass(classData);
    setFormData({
      name: classData.name,
      description: classData.description,
      time: classData.time,
      location: classData.location
    });
    setShowEditModal(true);
  };

  const handleDeleteClass = (classData) => {
    setSelectedClass(classData);
    setShowDeleteModal(true);
  };

  const submitAddClass = async () => {
    try {
      await axios.post(`${API}/classes`, formData);
      setShowAddModal(false);
      fetchClasses();
      if (onRefreshData) onRefreshData();
    } catch (error) {
      console.error('Failed to add class:', error);
      alert(error.response?.data?.detail || 'Failed to add class');
    }
  };

  const submitEditClass = async () => {
    try {
      await axios.put(`${API}/classes/${selectedClass.id}`, formData);
      setShowEditModal(false);
      fetchClasses();
      if (onRefreshData) onRefreshData();
    } catch (error) {
      console.error('Failed to update class:', error);
      alert(error.response?.data?.detail || 'Failed to update class');
    }
  };

  const submitDeleteClass = async () => {
    try {
      const response = await axios.delete(`${API}/classes/${selectedClass.id}`);
      setShowDeleteModal(false);
      fetchClasses();
      if (onRefreshData) onRefreshData();
      alert(`Class deleted successfully. ${response.data.students_updated} students updated.`);
    } catch (error) {
      console.error('Failed to delete class:', error);
      alert(error.response?.data?.detail || 'Failed to delete class');
    }
  };

  return (
    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg p-6 text-white mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">🏛️ Class Management</h2>
        <button
          onClick={handleAddClass}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm font-medium transition-all"
        >
          + Add New Class
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4">Loading classes...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((classData) => (
            <div key={classData.id} className="bg-white bg-opacity-20 hover:bg-opacity-30 p-4 rounded-lg transition-all">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">{classData.name}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditClass(classData)}
                    className="bg-blue-500 bg-opacity-70 hover:bg-opacity-90 p-1 rounded text-xs"
                    title="Edit Class"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteClass(classData)}
                    className="bg-red-500 bg-opacity-70 hover:bg-opacity-90 p-1 rounded text-xs"
                    title="Delete Class"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <p className="text-sm opacity-90 mb-1">{classData.description}</p>
              <p className="text-xs opacity-80">⏰ {classData.time}</p>
              <p className="text-xs opacity-80">📍 {classData.location}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add Class Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 text-gray-900">
            <h3 className="text-lg font-semibold mb-4">Add New Class</h3>
            
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Class Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                rows="3"
              />
              <input
                type="text"
                placeholder="Time (e.g., Mon/Wed 6:00 PM)"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={submitAddClass}
                disabled={!formData.name || !formData.description || !formData.time || !formData.location}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md"
              >
                Add Class
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Class Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 text-gray-900">
            <h3 className="text-lg font-semibold mb-4">Edit Class</h3>
            
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Class Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                rows="3"
              />
              <input
                type="text"
                placeholder="Time (e.g., Mon/Wed 6:00 PM)"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={submitEditClass}
                disabled={!formData.name || !formData.description || !formData.time || !formData.location}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md"
              >
                Update Class
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Class Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 text-gray-900">
            <h3 className="text-lg font-semibold mb-4">Delete Class</h3>
            <p className="mb-4">
              Are you sure you want to delete the class "{selectedClass?.name}"? 
              This will remove the class from all students' preferences but will not delete student accounts.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={submitDeleteClass}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
              >
                Delete Class
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Instructor Dashboard Component
const InstructorDashboard = ({ stats, attendance, announcements, onRefreshData }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();

  const handleAddStudent = () => {
    setShowAddModal(true);
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    // Refresh overall dashboard data including main stats
    if (onRefreshData) {
      onRefreshData();
    }
  };

  return (
    <div className="space-y-4 md:space-y-8">
      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-4 md:p-6 text-white">
        <h2 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">📋 Quick Actions</h2>
        <div className="grid grid-cols-3 gap-2 md:grid-cols-3 md:gap-4">
          <button
            onClick={handleAddStudent}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 md:p-4 rounded-lg text-center transition-all"
            data-testid="dashboard-add-student-btn"
          >
            <div className="text-xl md:text-2xl mb-1 md:mb-2">👥</div>
            <div className="font-medium text-xs md:text-base leading-tight">Enroll New Student</div>
            <div className="hidden md:block text-sm opacity-80">Register a new student</div>
          </button>
          
          <button
            onClick={() => navigate('/students')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 md:p-4 rounded-lg text-center transition-all"
            data-testid="dashboard-student-center-btn"
          >
            <div className="text-xl md:text-2xl mb-1 md:mb-2">📋</div>
            <div className="font-medium text-xs md:text-base leading-tight">Student Center</div>
            <div className="hidden md:block text-sm opacity-80">View and edit students</div>
          </button>
          
          <button
            onClick={() => navigate('/attendance')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 md:p-4 rounded-lg text-center transition-all"
            data-testid="dashboard-attendance-btn"
          >
            <div className="text-xl md:text-2xl mb-1 md:mb-2">✅</div>
            <div className="font-medium text-xs md:text-base leading-tight">Mark Attendance</div>
            <div className="hidden md:block text-sm opacity-80">Record class attendance</div>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <div 
          onClick={() => navigate('/students?filter=all')}
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3 md:p-6 text-white cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105"
          data-testid="stat-card-total-students"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm md:text-xl font-semibold leading-tight">Total Students</p>
              <p className="text-2xl md:text-3xl font-bold">{stats?.total_students || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-2 md:p-3 shrink-0 ml-2">
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div 
          onClick={() => navigate('/students?filter=regular')}
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-3 md:p-6 text-white cursor-pointer hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105"
          data-testid="stat-card-regular-students"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm md:text-xl font-semibold leading-tight">Regular Students</p>
              <p className="text-2xl md:text-3xl font-bold">{stats?.regular_students || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-2 md:p-3 shrink-0 ml-2">
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div 
          onClick={() => navigate('/students?filter=trial')}
          className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-3 md:p-6 text-white cursor-pointer hover:from-yellow-600 hover:to-yellow-700 transition-all transform hover:scale-105"
          data-testid="stat-card-trial-students"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm md:text-xl font-semibold leading-tight">Trial Students</p>
              <p className="text-2xl md:text-3xl font-bold">{stats?.trial_students || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-2 md:p-3 shrink-0 ml-2">
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div 
          onClick={() => navigate('/students?filter=vacation')}
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-3 md:p-6 text-white cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all transform hover:scale-105"
          data-testid="stat-card-vacation-students"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm md:text-xl font-semibold leading-tight">Students on Vacation</p>
              <p className="text-2xl md:text-3xl font-bold">{stats?.vacation_students || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-2 md:p-3 shrink-0 ml-2">
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Birthday Calendar - Modified Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <BirthdayCalendar />
        <div id="birthday-details-panel" className="bg-white rounded-lg shadow-sm p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Birthday Details</h3>
          <div className="text-center text-gray-500">
            <div className="mb-3 md:mb-4">
              <span className="text-3xl md:text-4xl">🎂</span>
            </div>
            <p className="text-sm md:text-base">Click on a calendar date to view birthday details</p>
          </div>
        </div>
      </div>

      {/* Add Student Modal */}
      {showAddModal && (
        <AddStudentModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}
    </div>
  );
};

// Student Dashboard Component
const StudentDashboard = ({ attendance, announcements }) => {
  const { user } = useAuth();

  return (
    <div className="space-y-8">
      {/* Student Info Card */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{user.name}</h2>
            <p className="text-purple-100">
              {user.belt_level} Belt • {user.is_trial ? 'Trial' : 'Regular'} Student
            </p>
          </div>
          <div className="bg-white bg-opacity-20 rounded-full p-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">My Attendance History</h3>
          <div className="space-y-3">
            {attendance.map((record, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{record.class_location.replace('_', ' ')}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(record.class_date).toLocaleDateString()}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  record.present 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {record.present ? 'Present' : 'Absent'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Announcements</h3>
          <div className="space-y-3">
            {announcements.map((announcement, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium">{announcement.title}</p>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    announcement.category === 'fee' ? 'bg-orange-100 text-orange-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {announcement.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{announcement.content.substring(0, 100)}...</p>
                <p className="text-xs text-gray-500 mt-2">
                  {new Date(announcement.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Announcements Management Component
const AnnouncementsManagement = () => {
  const { user } = useAuth();
  const [activeSubPage, setActiveSubPage] = useState('messages'); // 'messages' or 'sms'
  const [whatsappStatus, setWhatsappStatus] = useState('checking');
  const [connectedUser, setConnectedUser] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrDisplayed, setQrDisplayed] = useState(false); // New state for QR persistence
  const [sendingSMS, setSendingSMS] = useState({});
  const [whatsappResults, setWhatsappResults] = useState({});
  const [showLateFeeModal, setShowLateFeeModal] = useState(false);
  const [showMonthlyFeeModal, setShowMonthlyFeeModal] = useState(false);
  const [showWeatherCancelModal, setShowWeatherCancelModal] = useState(false);
  const [selectedMonthlyFeeClasses, setSelectedMonthlyFeeClasses] = useState([]);
  const [selectedLateFeeClasses, setSelectedLateFeeClasses] = useState([]);
  const [selectedWeatherCancelClasses, setSelectedWeatherCancelClasses] = useState([]);
  const [customMonthlyFeeMessage, setCustomMonthlyFeeMessage] = useState('');
  const [customLateFeeMessage, setCustomLateFeeMessage] = useState('');
  const [showSpecialMessageModal, setShowSpecialMessageModal] = useState(false);
  const [specialMessage, setSpecialMessage] = useState('');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [excludePaidStudents, setExcludePaidStudents] = useState(false);
  const [excludePaidNextMonth, setExcludePaidNextMonth] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-based index
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  
  // Welcome Message Settings State
  const [welcomeMessageEnabled, setWelcomeMessageEnabled] = useState(false);
  const [loadingWelcomeSettings, setLoadingWelcomeSettings] = useState(false);
  
  // Daily Message Usage State
  const [dailyUsage, setDailyUsage] = useState(null);

  // Automated Reminder Preview Modals State
  const [showPreDuePreview, setShowPreDuePreview] = useState(false);
  const [showOnDuePreview, setShowOnDuePreview] = useState(false);
  const [showPostDuePreview, setShowPostDuePreview] = useState(false);
  const [preDueMessage, setPreDueMessage] = useState('');
  const [onDueMessage, setOnDueMessage] = useState('');
  const [postDueMessage, setPostDueMessage] = useState('');

  // Default message templates
  const defaultMonthlyFeeMessage = `Hello {name} 👋

This is a friendly reminder that your monthly karate fee is due.

Please send your payment at your earliest convenience.

Thank you for your dedication to martial arts! 🥋

Sensei Chip
Austin Shotokan Karate Academy`;

  const defaultLateFeeMessage = `Good morning!

This is your friendly reminder that the karate tuition for October is now LATE. I would greatly appreciate it if you could send it to me by the end of the day today! 

Thank you! 
Sensei Chip`;

  // Default automated reminder templates
  const defaultPreDueMessage = `Good Morning, 
This is your friendly reminder that the karate tuition for [Next month] is due tomorrow.

Thank you,
Sensei Chip`;

  const defaultOnDueMessage = `Good Morning, 
This is your friendly reminder that the karate tuition for [Current month] is due today.

Thank you,
Sensei Chip`;

  const defaultPostDueMessage = `Good Morning, 
This is your friendly reminder that the karate tuition for [Current month] is now late. I would greatly appreciate it if you could send it to me by the end of the day today.

Thank you,
Sensei Chip`;

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Load classes from backend
  const [classLoadError, setClassLoadError] = useState(null);
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get(`${API}/classes`);
        const classes = response.data.map(cls => ({
          id: cls.id,  // Use actual class ID instead of fake ID
          name: cls.name,
          fullDetails: cls
        }));
        setClassOptions(classes);
        setClassLoadError(null);
      } catch (error) {
        console.error('Failed to fetch classes:', error);
        // Do NOT fall back to placeholder IDs — that leads to silent SMS failures
        // where the class ID never matches any real student enrollment.
        setClassOptions([]);
        setClassLoadError(
          error?.response?.data?.detail ||
          error?.message ||
          'Unable to load your classes. Please refresh the page. If the problem persists, contact support.'
        );
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClasses();
  }, []);

  // WhatsApp status checking functions (shared with WhatsAppSetup component)
  const checkSMSStatus = async () => {
    try {
      const response = await axios.get(`${API}/sms/status`);
      const data = response.data;
      
      console.log('Message Center SMS Status:', {
        status: data.status,
        connected: data.connected,
        user: data.user,
        instructorId: data.instructorId,
        phone_number: data.phone_number
      });
      
      // SMS is always ready if service is healthy
      if (data.connected === true || data.status === 'ready') {
        setWhatsappStatus('connected'); // Keep existing state variables for compatibility
        setConnectedUser(data.user);
        console.log('Message Center: SMS service is ready');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to check SMS status:', error);
      setWhatsappStatus('error');
      return false;
    }
  };

  // Check WhatsApp status when component mounts
  useEffect(() => {
    checkSMSStatus();
    
    // Poll for status updates with QR persistence protection
    const interval = setInterval(() => {
      // CRITICAL FIX: Don't poll if QR is displayed - prevents flashing
      if (qrDisplayed && qrCode && qrCode !== 'QR_CODE_MISSING') {
        console.log('Message Center: QR PERSISTENCE - Skipping WhatsApp status poll');
        return;
      }
      
      checkSMSStatus();
    }, 10000); // Increased to 10 seconds to reduce polling frequency

    return () => clearInterval(interval);
  }, []);

  // Fetch QR code function (shared with WhatsAppSetup component)
  const fetchQR = async () => {
    // CRITICAL FIX: Don't fetch QR if we already have one displayed
    // This prevents the flashing issue in Message Center
    if (qrDisplayed && qrCode && qrCode !== 'QR_CODE_MISSING') {
      console.log('Message Center: QR PERSISTENCE - Already have QR displayed, skipping fetch');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/whatsapp/qr`);
      const data = response.data;
      
      console.log('Message Center QR API Response:', {
        status: data.status,
        connected: data.connected,
        user: data.user,
        qr: data.qr ? 'QR Present' : 'No QR'
      });
      
      // Set QR code and mark as displayed for persistence
      if (data.qr && data.qr !== 'QR_CODE_MISSING') {
        setQrCode(data.qr);
        setQrDisplayed(true); // Mark QR as displayed for persistence
      }
      
      // Use same logic as checkStatus
      if (data.connected === true && data.user) {
        setWhatsappStatus('connected');
        setConnectedUser(data.user);
        setQrDisplayed(false); // Clear QR when connected
      } else {
        setWhatsappStatus(data.status || 'qr_ready');
        setConnectedUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
      setWhatsappStatus('error');
      setConnectedUser(null);
      setQrCode(null);
      setQrDisplayed(false);
    }
  };

  // Modified polling to prevent QR code flashing
  useEffect(() => {
    checkSMSStatus();
    
    const interval = setInterval(async () => {
      // CRITICAL FIX: Don't poll if QR is displayed - prevents flashing
      if (qrDisplayed && qrCode && qrCode !== 'QR_CODE_MISSING') {
        console.log('Message Center: QR PERSISTENCE - Skipping poll, QR displayed');
        return;
      }
      
      const isConnected = await checkSMSStatus();
      if (!isConnected && whatsappStatus !== 'connected') {
        console.log('Message Center: No QR available, fetching...');
        await fetchQR();
      }
    }, 10000); // Increased interval to 10 seconds to reduce polling

    return () => clearInterval(interval);
  }, []);

  // Welcome Message Settings Functions
  const loadWelcomeMessageSettings = async () => {
    try {
      const response = await axios.get(`${API}/welcome-message-settings`);
      setWelcomeMessageEnabled(response.data.welcome_message_enabled);
    } catch (error) {
      console.error('Failed to load welcome message settings:', error);
      setWelcomeMessageEnabled(false); // Default to false on error
    }
  };

  const updateWelcomeMessageSettings = async (enabled) => {
    setLoadingWelcomeSettings(true);
    try {
      const response = await axios.put(`${API}/welcome-message-settings`, {
        welcome_message_enabled: enabled
      });
      
      if (response.data.success) {
        setWelcomeMessageEnabled(enabled);
        // Show success notification
        console.log(`Welcome message ${enabled ? 'enabled' : 'disabled'} successfully`);
      }
    } catch (error) {
      console.error('Failed to update welcome message settings:', error);
      // Revert the toggle if update failed
      setWelcomeMessageEnabled(!enabled);
    } finally {
      setLoadingWelcomeSettings(false);
    }
  };

  // Daily Message Usage Functions
  const loadDailyUsage = async () => {
    try {
      const response = await axios.get(`${API}/daily-message-usage`);
      setDailyUsage(response.data);
    } catch (error) {
      console.error('Failed to load daily message usage:', error);
      // Set default values on error
      setDailyUsage({
        date: new Date().toISOString().split('T')[0],
        message_count: 0,
        daily_limit: 300,
        remaining_messages: 300,
        limit_reached: false
      });
    }
  };

  // SMS initialization function for Message Center
  const initializeWhatsApp = async () => {
    try {
      console.log('Message Center: Initializing SMS service...');
      const response = await axios.post(`${API}/sms/init`);
      
      if (response.data.success) {
        console.log('Message Center: SMS initialized successfully');
        setWhatsappStatus(response.data.status?.status || 'ready');
        return true;
      } else {
        console.error('Message Center: SMS initialization failed:', response.data);
        setWhatsappStatus('error');
        return false;
      }
    } catch (error) {
      console.error('Message Center: Failed to initialize SMS:', error);
      setWhatsappStatus('error');
      return false;
    }
  };

  // SMS disconnect function for Message Center  
  const disconnectWhatsApp = async () => {
    try {
      const response = await axios.post(`${API}/sms/disconnect`);
      
      if (response.data.success) {
        setWhatsappStatus('disconnected');
        setConnectedUser(null);
        setQrCode(null);
        setQrDisplayed(false);
        console.log('Message Center: SMS disconnected successfully');
      }
    } catch (error) {
      console.error('Message Center: Failed to disconnect SMS:', error);
    }
  };

  // Improved QR fetching with retries for Message Center (SMS - returns ready status)
  const fetchQRWithRetries = async (maxRetries = 8) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log(`Message Center: Checking SMS status - attempt ${attempt}/${maxRetries}`);
      
      try {
        // Fetch SMS status (no QR needed for SMS)
        const response = await axios.get(`${API}/sms/qr`);
        console.log(`Message Center: SMS Status Response attempt ${attempt}:`, {
          status: response.data.status,
          connected: response.data.connected,
          service: response.data.service
        });
        
        // SMS is always ready, no QR code needed
        if (response.data.connected === true || response.data.status === 'ready') {
          console.log(`Message Center: SMS service is ready on attempt ${attempt}!`);
          
          // Set connected status (no QR for SMS)
          setQrCode(null);
          setQrDisplayed(false);
          setWhatsappStatus('connected');
          
          return true;
        }
        
        // If service not ready yet, wait before next attempt
        if (attempt < maxRetries) {
          const waitTime = Math.min(2000 + (attempt * 500), 4000); // Shorter wait for SMS
          console.log(`Message Center: SMS not ready yet, waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      } catch (error) {
        console.error(`Message Center: SMS status check attempt ${attempt} failed:`, error);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    
    console.error('Message Center: Failed to verify SMS service after all retries');
    return false;
  };

  // Loading state for WhatsApp operations
  const [loading, setLoading] = useState(false);

  // Load welcome message settings and daily usage when component mounts
  useEffect(() => {
    loadWelcomeMessageSettings();
    loadDailyUsage();
  }, []);

  const announcementTypes = [
    {
      id: 'monthly_fee',
      title: 'Monthly Fee',
      icon: '💰',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      message: `Hello {name} 👋

This is a friendly reminder about upcoming Monthly Fees, please send your payment at your earliest convenience.

If you've already paid, please disregard this message.

Thank you for your dedication to martial arts! 🙏

Sensei Chip
Austin Shotokan Karate Academy`
    },
    {
      id: 'late_fee',
      title: 'Late Fee',
      icon: '⚠️',
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      message: `Hello {name} 👋

This is a friendly reminder that you fee for this month's Karate class is yet to be paid.
Please send your payment at your earliest convenience.
Thank you for your dedication to martial arts! 🙏

Sensei Chip
Austin Shotokan Karate Academy`
    },
    {
      id: 'weather_cancel',
      title: 'Weather Cancel',
      icon: '🌧️',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      message: `Hello {name} 👋

Due to inclement weather conditions, classes for today have been cancelled.

Please stay safe and reach out to me through text if you have any questions.

Thank you for your dedication to martial arts! 🙏

Sensei Chip
Austin Shotokan Karate Academy`
    },
    {
      id: 'special_announcement',
      title: 'Special Announcement',
      icon: '📢',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      message: null // Will be custom message
    },
    {
      id: 'pre_due_reminder',
      title: 'Pre Due Reminder',
      icon: '📅',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      message: 'Automated reminder sent 1 day before month starts',
      isAutomated: true
    },
    {
      id: 'on_due_reminder',
      title: 'On Due Reminder',
      icon: '🔔',
      color: 'bg-yellow-500',
      hoverColor: 'hover:bg-yellow-600',
      message: 'Automated reminder sent on 1st day of month',
      isAutomated: true
    },
    {
      id: 'post_due_reminder',
      title: 'Post Due Reminder',
      icon: '⏰',
      color: 'bg-red-600',
      hoverColor: 'hover:bg-red-700',
      message: 'Automated reminder sent on 2nd day of month',
      isAutomated: true
    }
  ];

  const handleSendSMS = async (type) => {
    if (type.id === 'late_fee') {
      // Initialize with default message if not already set
      if (!customLateFeeMessage) {
        setCustomLateFeeMessage(defaultLateFeeMessage);
      }
      setShowLateFeeModal(true);
      return;
    }
    
    if (type.id === 'monthly_fee') {
      // Initialize with default message if not already set
      if (!customMonthlyFeeMessage) {
        setCustomMonthlyFeeMessage(defaultMonthlyFeeMessage);
      }
      setShowMonthlyFeeModal(true);
      return;
    }

    if (type.id === 'weather_cancel') {
      setShowWeatherCancelModal(true);
      return;
    }

    // Handle automated reminders
    if (type.isAutomated) {
      handleAutomatedReminder(type);
      return;
    }

    setSendingSMS(prev => ({ ...prev, [type.id]: true }));
    
    try {
      const requestData = {
        message_type: type.id,
        custom_message: type.message
      };

      const response = await axios.post(`${API}/sms/send-bulk-message`, requestData);
      
      const apiSuccess = response.data && response.data.success !== false && (response.data.successful_sends || response.data.success_count || 0) > 0;
      setWhatsappResults(prev => ({ 
        ...prev, 
        [type.id]: {
          success: apiSuccess,
          data: response.data,
          error: apiSuccess ? undefined : (response.data?.error || 'No recipients matched — check that students exist, are not on vacation, and (for fee messages) have a monthly payment amount set.')
        }
      }));
      
      // Refresh daily usage after successful send
      loadDailyUsage();
      
      // Clear result after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults[type.id];
          return newResults;
        });
      }, 5000);
      
    } catch (error) {
      console.error('Failed to send WhatsApp:', error);
      setWhatsappResults(prev => ({ 
        ...prev, 
        [type.id]: {
          success: false,
          error: error.response?.data?.detail || 'Failed to send SMS messages'
        }
      }));
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults[type.id];
          return newResults;
        });
      }, 5000);
    } finally {
      setSendingSMS(prev => ({ ...prev, [type.id]: false }));
    }
  };

  const handleAutomatedReminder = async (type) => {
    setSendingSMS(prev => ({ ...prev, [type.id]: true }));
    
    try {
      let endpoint;
      switch (type.id) {
        case 'pre_due_reminder':
          endpoint = `${API}/sms/send-pre-due-reminder`;
          break;
        case 'on_due_reminder':
          endpoint = `${API}/sms/send-on-due-reminder`;
          break;
        case 'post_due_reminder':
          endpoint = `${API}/sms/send-post-due-reminder`;
          break;
        default:
          throw new Error('Invalid automated reminder type');
      }

      const response = await axios.post(endpoint);
      
      setWhatsappResults(prev => ({ 
        ...prev, 
        [type.id]: {
          success: true,
          data: response.data
        }
      }));
      
      // Refresh daily usage after successful send
      loadDailyUsage();
      
      // Clear result after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults[type.id];
          return newResults;
        });
      }, 5000);
      
    } catch (error) {
      console.error('Failed to send automated reminder:', error);
      setWhatsappResults(prev => ({ 
        ...prev, 
        [type.id]: {
          success: false,
          error: error.response?.data?.message || 'Failed to send automated reminder'
        }
      }));
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults[type.id];
          return newResults;
        });
      }, 5000);
    } finally {
      setSendingSMS(prev => ({ ...prev, [type.id]: false }));
    }
  };

  const handleSendLateFee = async () => {
    setSendingSMS(prev => ({ ...prev, late_fee: true }));
    
    try {
      const requestData = {
        message_type: 'late_fee',
        selected_month: selectedMonth,
        selected_year: selectedYear,
        custom_message: customLateFeeMessage
      };

      const response = await axios.post(`${API}/sms/send-bulk-message`, requestData);
      
      const apiSuccess = response.data && response.data.success !== false && (response.data.successful_sends || response.data.success_count || 0) > 0;
      setWhatsappResults(prev => ({ 
        ...prev, 
        late_fee: {
          success: apiSuccess,
          data: response.data,
          error: apiSuccess ? undefined : (response.data?.error || 'No unpaid students found for the selected month, or none have a monthly fee amount > 0.')
        }
      }));
      
      // Refresh daily usage after successful send
      loadDailyUsage();
      
      setShowLateFeeModal(false);
      
      // Clear result after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults.late_fee;
          return newResults;
        });
      }, 5000);
      
    } catch (error) {
      console.error('Failed to send WhatsApp:', error);
      setWhatsappResults(prev => ({ 
        ...prev, 
        late_fee: {
          success: false,
          error: error.response?.data?.detail || 'Failed to send SMS messages'
        }
      }));
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults.late_fee;
          return newResults;
        });
      }, 5000);
    } finally {
      setSendingSMS(prev => ({ ...prev, late_fee: false }));
    }
  };

  const handleSendSpecialAnnouncement = async () => {
    if (!specialMessage.trim()) {
      alert('Please enter a message for the special announcement.');
      return;
    }

    if (selectedClasses.length === 0) {
      alert('Please select at least one class to send the message to.');
      return;
    }

    setSendingSMS(prev => ({ ...prev, special_announcement: true }));
    
    try {
      const response = await axios.post(`${API}/sms/send-bulk-message`, {
        message_type: 'special_announcement',
        custom_message: specialMessage,
        selected_classes: selectedClasses,
        exclude_paid_students: excludePaidStudents,
        exclude_paid_next_month: excludePaidNextMonth
      });
      
      const apiSuccess = response.data && response.data.success !== false && (response.data.successful_sends || response.data.success_count || 0) > 0;
      setWhatsappResults(prev => ({ 
        ...prev, 
        special_announcement: {
          success: apiSuccess,
          data: response.data,
          error: apiSuccess ? undefined : (response.data?.error || 'No recipients matched — check your class selection and payment filters.')
        }
      }));
      
      // Refresh daily usage after successful send
      loadDailyUsage();
      
      setShowSpecialMessageModal(false);
      setSpecialMessage('');
      setSelectedClasses([]);
      setExcludePaidStudents(false);
      setExcludePaidNextMonth(false);
      
      // Clear result after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults.special_announcement;
          return newResults;
        });
      }, 5000);
      
    } catch (error) {
      console.error('Failed to send WhatsApp:', error);
      setWhatsappResults(prev => ({ 
        ...prev, 
        special_announcement: {
          success: false,
          error: error.response?.data?.detail || 'Failed to send SMS messages'
        }
      }));
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults.special_announcement;
          return newResults;
        });
      }, 5000);
    } finally {
      setSendingSMS(prev => ({ ...prev, special_announcement: false }));
    }
  };

  const handleClassSelection = (classId) => {
    setSelectedClasses(prev => {
      if (prev.includes(classId)) {
        return prev.filter(id => id !== classId);
      } else {
        return [...prev, classId];
      }
    });
  };

  const handleSendMonthlyFee = async () => {
    setSendingSMS(prev => ({ ...prev, monthly_fee: true }));
    
    try {
      const requestData = {
        message_type: 'monthly_fee',
        selected_classes: selectedMonthlyFeeClasses.length === 0 ? null : selectedMonthlyFeeClasses, // null means all classes
        custom_message: customMonthlyFeeMessage
      };

      const response = await axios.post(`${API}/sms/send-bulk-message`, requestData);
      
      const apiSuccess = response.data && response.data.success !== false && (response.data.successful_sends || response.data.success_count || 0) > 0;
      setWhatsappResults(prev => ({ 
        ...prev, 
        monthly_fee: {
          success: apiSuccess,
          data: response.data,
          error: apiSuccess ? undefined : (response.data?.error || 'No unpaid students found, or none have a monthly fee amount > 0.')
        }
      }));
      
      // Refresh daily usage after successful send
      loadDailyUsage();
      
      setShowMonthlyFeeModal(false);
      setSelectedMonthlyFeeClasses([]);
      // Keep the custom message for reuse
      // Keep the custom message for next time - don't reset it
      
      // Clear result after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults.monthly_fee;
          return newResults;
        });
      }, 5000);
      
    } catch (error) {
      console.error('Failed to send monthly fee reminders:', error);
      setWhatsappResults(prev => ({ 
        ...prev, 
        monthly_fee: {
          success: false,
          error: error.response?.data?.detail || 'Failed to send monthly fee reminders'
        }
      }));
    } finally {
      setSendingSMS(prev => ({ ...prev, monthly_fee: false }));
    }
  };

  const handleMonthlyFeeClassToggle = (classId) => {
    setSelectedMonthlyFeeClasses(prev => {
      if (prev.includes(classId)) {
        // Removing a class
        const newSelection = prev.filter(id => id !== classId);
        return newSelection;
      } else {
        // Adding a class - this automatically unchecks "All Students"
        return [...prev, classId];
      }
    });
  };

  const handleLateFeeClassToggle = (classId) => {
    setSelectedLateFeeClasses(prev => {
      if (prev.includes(classId)) {
        // Removing a class
        const newSelection = prev.filter(id => id !== classId);
        return newSelection;
      } else {
        // Adding a class - this automatically unchecks "All Students"
        return [...prev, classId];
      }
    });
  };

  const handleWeatherCancelClassToggle = (classId) => {
    setSelectedWeatherCancelClasses(prev => {
      if (prev.includes(classId)) {
        // Removing a class
        const newSelection = prev.filter(id => id !== classId);
        return newSelection;
      } else {
        // Adding a class - this automatically unchecks "All Students"
        return [...prev, classId];
      }
    });
  };

  const handleSelectAllMonthlyFeeClasses = () => {
    if (selectedMonthlyFeeClasses.length === classOptions.length) {
      // If all are selected, deselect all
      setSelectedMonthlyFeeClasses([]);
    } else {
      // Select all classes
      setSelectedMonthlyFeeClasses(classOptions.map(cls => cls.id));
    }
  };

  const handleSelectAllLateFeeClasses = () => {
    if (selectedLateFeeClasses.length === classOptions.length) {
      setSelectedLateFeeClasses([]);
    } else {
      setSelectedLateFeeClasses(classOptions.map(cls => cls.id));
    }
  };

  const handleSelectAllWeatherCancelClasses = () => {
    if (selectedWeatherCancelClasses.length === classOptions.length) {
      setSelectedWeatherCancelClasses([]);
    } else {
      setSelectedWeatherCancelClasses(classOptions.map(cls => cls.id));
    }
  };

  const handleSendWeatherCancel = async () => {
    setSendingSMS(prev => ({ ...prev, weather_cancel: true }));
    
    try {
      const requestData = {
        message_type: 'weather_cancel',
        selected_classes: selectedWeatherCancelClasses.length === 0 ? null : selectedWeatherCancelClasses, // null means all classes
        custom_message: `🌧️ Class Cancellation Notice

Hello {name},

Due to severe weather conditions, all karate classes for today have been cancelled for safety reasons.

We will resume regular classes as scheduled. Stay safe!

Sensei Chip
Austin Shotokan Karate Academy`
      };

      const response = await axios.post(`${API}/sms/send-bulk-message`, requestData);
      
      const apiSuccess = response.data && response.data.success !== false && (response.data.successful_sends || response.data.success_count || 0) > 0;
      setWhatsappResults(prev => ({ 
        ...prev, 
        weather_cancel: {
          success: apiSuccess,
          data: response.data,
          error: apiSuccess ? undefined : (response.data?.error || 'No recipients — all students may be on vacation or none exist in the selected classes.')
        }
      }));
      
      // Refresh daily usage after successful send
      loadDailyUsage();
      
      setShowWeatherCancelModal(false);
      setSelectedWeatherCancelClasses([]);
      
      // Clear result after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults.weather_cancel;
          return newResults;
        });
      }, 5000);
      
    } catch (error) {
      console.error('Failed to send weather cancellation:', error);
      setWhatsappResults(prev => ({ 
        ...prev, 
        weather_cancel: {
          success: false,
          error: error.response?.data?.detail || 'Failed to send weather cancellation'
        }
      }));
    } finally {
      setSendingSMS(prev => ({ ...prev, weather_cancel: false }));
    }
  };

  if (!user.is_instructor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600">Only instructors can access announcement management.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        <div className="mb-4 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Message Center</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">Send SMS messages to students</p>
        </div>

        {/* Sub-navigation tabs */}
        <div className="border-b border-gray-200 mb-4 md:mb-6">
          <nav className="-mb-px flex space-x-4 md:space-x-8">
            <button
              onClick={() => setActiveSubPage('messages')}
              className={`py-2 px-1 border-b-2 font-medium text-xs md:text-sm ${
                activeSubPage === 'messages'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="msg-tab-messages"
            >
              📢 SMS Messages
            </button>
            <button
              onClick={() => {
                console.log('SMS Setup tab clicked, setting activeSubPage to sms');
                setActiveSubPage('sms');
                // Force re-render by also updating a dummy state
                setTimeout(() => {
                  console.log('Current activeSubPage after click:', activeSubPage);
                }, 100);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-xs md:text-sm ${
                activeSubPage === 'sms'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="msg-tab-sms-setup"
            >
              📱 SMS Setup
            </button>
          </nav>
        </div>

        {/* Messages Tab Content */}
        {activeSubPage === 'messages' && (
          <div>
            {/* Welcome Message Settings */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
              <div className="flex items-start md:items-center justify-between gap-3">
                <div className="flex items-start md:items-center min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">👋</span>
                    </div>
                  </div>
                  <div className="ml-3 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900">Welcome Message for New Students</h4>
                    <p className="text-xs md:text-sm text-gray-600">
                      Automatically send SMS welcome messages when new students are enrolled
                    </p>
                  </div>
                </div>
                <div className="flex items-center shrink-0">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={welcomeMessageEnabled}
                        onChange={(e) => updateWelcomeMessageSettings(e.target.checked)}
                        disabled={loadingWelcomeSettings}
                        className="sr-only"
                        data-testid="welcome-msg-toggle"
                      />
                      <div className={`block w-14 h-8 rounded-full transition-colors duration-200 ease-in-out ${
                        welcomeMessageEnabled 
                          ? 'bg-green-500' 
                          : 'bg-gray-300'
                      } ${loadingWelcomeSettings ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-200 ease-in-out ${
                          welcomeMessageEnabled 
                            ? 'transform translate-x-6' 
                            : ''
                        }`}></div>
                      </div>
                    </div>
                    <span className="hidden md:inline ml-3 text-sm font-medium text-gray-900">
                      {loadingWelcomeSettings ? 'Updating...' : (welcomeMessageEnabled ? 'Enabled' : 'Disabled')}
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Status Message */}
              <div className="mt-3 pl-11">
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  welcomeMessageEnabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    welcomeMessageEnabled ? 'bg-green-400' : 'bg-gray-400'
                  }`}></div>
                  {welcomeMessageEnabled 
                    ? 'New students will receive welcome messages' 
                    : 'Welcome messages are disabled for new students'
                  }
                </div>
              </div>
            </div>

            {/* Daily Message Usage Section */}
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">📊</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-gray-900">Daily Message Usage</h4>
                    <p className="text-sm text-gray-600">
                      Track your daily SMS message usage {dailyUsage ? `(Limit: ${dailyUsage.daily_limit} messages/day)` : '(Loading...)'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center ml-4">
                  {dailyUsage ? (
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        dailyUsage.limit_reached 
                          ? 'text-red-600' 
                          : dailyUsage.remaining_messages < 50 
                            ? 'text-orange-600' 
                            : 'text-green-600'
                      }`}>
                        {dailyUsage.message_count} / {dailyUsage.daily_limit}
                      </div>
                      <div className="text-xs text-gray-500">
                        {dailyUsage.remaining_messages} remaining
                      </div>
                    </div>
                  ) : (
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-400">-- / --</div>
                      <div className="text-xs text-gray-400">Loading...</div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3 pl-11">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      dailyUsage?.limit_reached 
                        ? 'bg-red-500' 
                        : dailyUsage && dailyUsage.message_count / dailyUsage.daily_limit > 0.8 
                          ? 'bg-orange-500' 
                          : 'bg-green-500'
                    }`}
                    style={{
                      width: dailyUsage 
                        ? `${Math.min((dailyUsage.message_count / dailyUsage.daily_limit) * 100, 100)}%` 
                        : '0%'
                    }}
                  ></div>
                </div>
                {dailyUsage?.limit_reached && (
                  <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-red-400"></div>
                    Daily limit reached. Resets at midnight UTC.
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Fee and Late Fee buttons moved to Automated Reminders section */}

        {/* Automated Reminders Section */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white mb-8">
          <h2 className="text-xl font-semibold mb-4">🤖 Automated Reminders</h2>
          <p className="text-sm opacity-90 mb-4">
            These reminders are automatically scheduled to send at 10 AM CST on specific dates to students who haven't paid.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* Pre Due Reminder Button */}
            <button
              onClick={() => {
                setPreDueMessage(defaultPreDueMessage);
                setShowPreDuePreview(true);
              }}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 p-4 rounded-lg text-center transition-all"
            >
              <div className="text-2xl mb-2">📅</div>
              <div className="font-medium">Pre Due Reminder</div>
              <div className="text-xs opacity-80">Preview Template</div>
            </button>

            {/* On Due Reminder Button */}
            <button
              onClick={() => {
                setOnDueMessage(defaultOnDueMessage);
                setShowOnDuePreview(true);
              }}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 p-4 rounded-lg text-center transition-all"
            >
              <div className="text-2xl mb-2">🔔</div>
              <div className="font-medium">On Due Reminder</div>
              <div className="text-xs opacity-80">Preview Template</div>
            </button>

            {/* Post Due Reminder Button */}
            <button
              onClick={() => {
                setPostDueMessage(defaultPostDueMessage);
                setShowPostDuePreview(true);
              }}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 p-4 rounded-lg text-center transition-all"
            >
              <div className="text-2xl mb-2">⏰</div>
              <div className="font-medium">Post Due Reminder</div>
              <div className="text-xs opacity-80">Preview Template</div>
            </button>
          </div>

          {/* Automated Reminders Results */}
          <div className="mt-4 space-y-2">
            {['pre_due_reminder', 'on_due_reminder', 'post_due_reminder'].map(messageType => (
              whatsappResults[messageType] && (
                <div key={messageType} className={`p-3 rounded-lg ${whatsappResults[messageType].success ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'}`}>
                  {whatsappResults[messageType].success ? (
                    <div className="text-sm">
                      <p className="font-medium">✅ {messageType.replace(/_/g, ' ').toUpperCase()} sent successfully!</p>
                      <p>Sent to {whatsappResults[messageType].data?.total_recipients || 0} students</p>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <p className="font-medium">❌ Failed to send {messageType.replace(/_/g, ' ').toUpperCase()}</p>
                      <p>{typeof whatsappResults[messageType].error === 'string' ? whatsappResults[messageType].error : JSON.stringify(whatsappResults[messageType].error)}</p>
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        </div>

        {/* Custom Announcements Section */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-6 text-white mb-8">
          <h2 className="text-xl font-semibold mb-4">📝 Custom Announcements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setShowSpecialMessageModal(true)}
              disabled={dailyUsage?.limit_reached}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 disabled:opacity-50 p-6 rounded-lg text-center transition-all"
            >
              <div className="text-3xl mb-2">📢</div>
              <div className="font-medium text-lg">Special Message</div>
              <div className="text-sm opacity-80">Send custom message to selected classes</div>
            </button>
            
            {/* Weather Cancellation Button */}
            <button
              onClick={() => handleSendSMS(announcementTypes.find(t => t.id === 'weather_cancel'))}
              disabled={sendingSMS.weather_cancel || dailyUsage?.limit_reached}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 disabled:opacity-50 p-6 rounded-lg text-center transition-all"
            >
              {sendingSMS.weather_cancel ? (
                <>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <div className="font-medium">Sending...</div>
                </>
              ) : (
                <>
                  <div className="text-3xl mb-2">🌧️</div>
                  <div className="font-medium text-lg">Weather Cancellation</div>
                  <div className="text-sm opacity-80">Send to all students</div>
                </>
              )}
            </button>
          </div>

          {/* Custom Announcements Results */}
          <div className="mt-4 space-y-2">
            {['special_announcement', 'weather_cancel'].map(messageType => (
              whatsappResults[messageType] && (
                <div key={messageType} className={`p-3 rounded-lg ${whatsappResults[messageType].success ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'}`}>
                  {whatsappResults[messageType].success ? (
                    <div className="text-sm">
                      <p className="font-medium">✅ {messageType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} sent successfully!</p>
                      <p>Sent to {whatsappResults[messageType].data?.success_count || whatsappResults[messageType].data?.successful_sends || 0} students</p>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <p className="font-medium">❌ Failed to send {messageType.replace(/_/g, ' ')}</p>
                      <p>{typeof whatsappResults[messageType].error === 'string' ? whatsappResults[messageType].error : JSON.stringify(whatsappResults[messageType].error)}</p>
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        </div>

      {/* Special Message Modal */}
      {showSpecialMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Special Message</h3>
            
            {/* Message Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Message:
              </label>
              <textarea
                value={specialMessage}
                onChange={(e) => setSpecialMessage(e.target.value)}
                placeholder="Type your custom message here..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows="4"
              />
            </div>
            
            {/* Class Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Classes: <span className="text-red-500">*</span>
              </label>
              {classLoadError && (
                <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800 font-medium">⚠️ Could not load classes</p>
                  <p className="text-xs text-red-700 mt-1">{classLoadError}</p>
                </div>
              )}
              {!classLoadError && classOptions.length === 0 && !loadingClasses && (
                <div className="mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">You have no classes yet. Create one in Class Center first, then enroll students.</p>
                </div>
              )}
              <div className="space-y-2">
                {classOptions.map((classOption) => (
                  <label key={classOption.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedClasses.includes(classOption.id)}
                      onChange={() => handleClassSelection(classOption.id)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      data-testid={`special-msg-class-${classOption.id}`}
                    />
                    <span className="ml-2 text-sm text-gray-700">{classOption.name}</span>
                  </label>
                ))}
              </div>
              {selectedClasses.length === 0 && classOptions.length > 0 && (
                <p className="text-red-500 text-xs mt-1">Please select at least one class</p>
              )}
            </div>

            {/* Payment Filter Options */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Payment Filters:</div>
              
              {/* Current Month Filter */}
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="excludePaidStudents"
                  checked={excludePaidStudents}
                  onChange={(e) => setExcludePaidStudents(e.target.checked)}
                  className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="excludePaidStudents" className="text-sm font-medium text-gray-700">
                  Exclude students who have paid for {new Date().toLocaleString('default', { month: 'long' })} (current month)
                </label>
              </div>
              
              {/* Next Month Filter */}
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="excludePaidNextMonth"
                  checked={excludePaidNextMonth}
                  onChange={(e) => setExcludePaidNextMonth(e.target.checked)}
                  className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="excludePaidNextMonth" className="text-sm font-medium text-gray-700">
                  Exclude students who have paid for {new Date(new Date().setMonth(new Date().getMonth() + 1)).toLocaleString('default', { month: 'long' })} (next month)
                </label>
              </div>
              
              <div className="text-xs text-gray-500 mt-1 ml-6">
                Select filters to exclude students who have already paid for specific months from receiving this message
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowSpecialMessageModal(false);
                  setSpecialMessage('');
                  setSelectedClasses([]);
                  setExcludePaidStudents(false);
                  setExcludePaidNextMonth(false);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSendSpecialAnnouncement}
                disabled={sendingSMS.special_announcement || !specialMessage.trim() || selectedClasses.length === 0 || dailyUsage?.limit_reached}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white px-4 py-2 rounded-md"
              >
                {sendingSMS.special_announcement ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
        )}
        
        {/* SMS Setup Tab Content */}
        {activeSubPage === 'sms' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">📱 SMS Integration</h3>
              <p className="text-gray-600">Configure your SMS service for sending messages to students.</p>
              <p className="text-sm text-green-600 mt-2">✅ SMS service is ready for use!</p>
            </div>

            {/* SMS Service Status */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-green-800">SMS Service Active</h4>
                  <p className="text-sm text-green-700">Ready to send SMS messages to students</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h5 className="font-medium text-gray-900 mb-2">Usage Instructions:</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Use the "📢 SMS Messages" tab to send messages to students</li>
                <li>• Messages are sent based on payment status and class enrollment</li>
                <li>• Trial students are automatically excluded from payment reminders</li>
                <li>• You can send custom announcements to specific classes</li>
              </ul>
            </div>
          </div>
        )}

      {/* Late Fee Modal */}
      {showLateFeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">⚠️ Send Late Fee Reminder</h3>
            
            <p className="text-gray-600 mb-4">
              Customize your late fee reminder message and select the month/year.
            </p>
            
            {/* Month and Year Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Month and Year for Late Fee:
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    {['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                      <option key={index} value={index}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    {[2023, 2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Message Editor */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Content: <span className="text-red-500">*</span>
              </label>
              <textarea
                value={customLateFeeMessage}
                onChange={(e) => setCustomLateFeeMessage(e.target.value)}
                placeholder="Enter your late fee reminder message..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows="8"
              />
              <div className="mt-2 flex items-start space-x-4">
                <div className="text-xs text-gray-500">
                  💡 <strong>Tip:</strong> Use <code>{'{name}'}</code> to personalize with student names
                </div>
                <button
                  onClick={() => setCustomLateFeeMessage(defaultLateFeeMessage)}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                >
                  Reset to Default
                </button>
              </div>
            </div>

            {/* Message Preview */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview (how it will look to students):
              </label>
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {customLateFeeMessage.replace('{name}', 'John Smith') || 'Enter a message above to see preview...'}
                </div>
              </div>
            </div>

            {/* Class Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Classes:
              </label>
              
              {/* All Students Checkbox */}
              <div className="mb-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedLateFeeClasses.length === 0}
                    onChange={() => setSelectedLateFeeClasses([])}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-900">All Students (All Classes)</span>
                </label>
              </div>
              
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-500 mb-2">Or select specific classes:</p>
                
                {loadingClasses ? (
                  <div className="text-sm text-gray-500">Loading classes...</div>
                ) : classOptions.length > 0 ? (
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {classOptions.map(cls => (
                      <label key={cls.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedLateFeeClasses.includes(cls.id)}
                          onChange={() => handleLateFeeClassToggle(cls.id)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{cls.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No classes available</div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowLateFeeModal(false);
                  setSelectedLateFeeClasses([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSendLateFee}
                disabled={sendingSMS.late_fee || !customLateFeeMessage.trim() || dailyUsage?.limit_reached}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-4 py-2 rounded-md"
              >
                {sendingSMS.late_fee ? 'Sending...' : 'Send Late Fee Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Fee Modal */}
      {showMonthlyFeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 Send Monthly Fee Reminder</h3>
            
            <p className="text-gray-600 mb-4">
              Customize your monthly fee reminder message and select which classes should receive it.
            </p>
            
            {/* Message Editor */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Content: <span className="text-red-500">*</span>
              </label>
              <textarea
                value={customMonthlyFeeMessage}
                onChange={(e) => setCustomMonthlyFeeMessage(e.target.value)}
                placeholder="Enter your monthly fee reminder message..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="8"
              />
              <div className="mt-2 flex items-start space-x-4">
                <div className="text-xs text-gray-500">
                  💡 <strong>Tip:</strong> Use <code>{'{name}'}</code> to personalize with student names
                </div>
                <button
                  onClick={() => setCustomMonthlyFeeMessage(defaultMonthlyFeeMessage)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Reset to Default
                </button>
              </div>
            </div>

            {/* Message Preview */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview (how it will look to students):
              </label>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {customMonthlyFeeMessage.replace('{name}', 'John Smith') || 'Enter a message above to see preview...'}
                </div>
              </div>
            </div>
            
            {/* Class Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Classes:
              </label>
              
              {/* All Students Checkbox */}
              <div className="mb-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedMonthlyFeeClasses.length === 0}
                    onChange={() => setSelectedMonthlyFeeClasses([])}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-900">All Students (All Classes)</span>
                </label>
              </div>
              
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-500 mb-2">Or select specific classes:</p>
                
                {loadingClasses ? (
                  <div className="text-sm text-gray-500">Loading classes...</div>
                ) : classOptions.length > 0 ? (
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {classOptions.map(cls => (
                      <label key={cls.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedMonthlyFeeClasses.includes(cls.id)}
                          onChange={() => handleMonthlyFeeClassToggle(cls.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{cls.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No classes available</div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowMonthlyFeeModal(false);
                  setSelectedMonthlyFeeClasses([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMonthlyFee}
                disabled={sendingSMS.monthly_fee || !customMonthlyFeeMessage.trim() || dailyUsage?.limit_reached}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md"
              >
                {sendingSMS.monthly_fee ? 'Sending...' : 'Send Monthly Fee Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weather Cancel Modal */}
      {showWeatherCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🌧️ Send Weather Cancellation Notice</h3>
            
            <p className="text-gray-600 mb-4">
              Select which classes should receive the weather cancellation notice, or leave none selected to send to all students.
            </p>
            
            {/* Class Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Classes:
              </label>
              
              {/* All Students Checkbox */}
              <div className="mb-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedWeatherCancelClasses.length === 0}
                    onChange={() => setSelectedWeatherCancelClasses([])}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-900">All Students (All Classes)</span>
                </label>
              </div>
              
              <div className="border-t border-gray-200 pt-2">
                <p className="text-xs text-gray-500 mb-2">Or select specific classes:</p>
                
                {loadingClasses ? (
                  <div className="text-sm text-gray-500">Loading classes...</div>
                ) : classOptions.length > 0 ? (
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {classOptions.map(cls => (
                      <label key={cls.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedWeatherCancelClasses.includes(cls.id)}
                          onChange={() => handleWeatherCancelClassToggle(cls.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{cls.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No classes available</div>
                )}
              </div>
            </div>
            
            {/* Message Preview */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Preview:
              </label>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                <p><strong>🌧️ Class Cancellation Notice</strong></p>
                <br />
                <p>Hello [Student Name],</p>
                <br />
                <p>Due to severe weather conditions, all karate classes for today have been cancelled for safety reasons.</p>
                <br />
                <p>We will resume regular classes as scheduled. Stay safe!</p>
                <br />
                <p><strong>Sensei Chip</strong><br />Austin Shotokan Karate Academy</p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowWeatherCancelModal(false);
                  setSelectedWeatherCancelClasses([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSendWeatherCancel}
                disabled={sendingSMS.weather_cancel || dailyUsage?.limit_reached}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-4 py-2 rounded-md"
              >
                {sendingSMS.weather_cancel ? 'Sending...' : 'Send Cancellation Notice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre Due Reminder Preview Modal */}
      {showPreDuePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📅 Pre Due Reminder Template</h3>
            
            <p className="text-gray-600 mb-4">
              This message will be sent automatically 1 day before each month starts at 10 AM CST to students who haven't paid for the upcoming month.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Template (View Only):
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-800 whitespace-pre-line text-sm">
                {preDueMessage}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                👁️ This template is automatically sent and cannot be edited through this interface.
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPreDuePreview(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* On Due Reminder Preview Modal */}
      {showOnDuePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🔔 On Due Reminder Template</h3>
            
            <p className="text-gray-600 mb-4">
              This message will be sent automatically on the 1st day of each month at 10 AM CST to students who haven't paid for the current month.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Template (View Only):
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-800 whitespace-pre-line text-sm">
                {onDueMessage}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                👁️ This template is automatically sent and cannot be edited through this interface.
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowOnDuePreview(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post Due Reminder Preview Modal */}
      {showPostDuePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">⏰ Post Due Reminder Template</h3>
            
            <p className="text-gray-600 mb-4">
              This message will be sent automatically on the 2nd day of each month at 10 AM CST to students who haven't paid for the current month.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Template (View Only):
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-800 whitespace-pre-line text-sm">
                {postDueMessage}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                👁️ This template is automatically sent and cannot be edited through this interface.
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPostDuePreview(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Student Center Component (formerly StudentsManagement)
const ManageStudents = () => {
  const location = useLocation();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingStudent, setEditingStudent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showAttendanceSubpage, setShowAttendanceSubpage] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Handle URL filter parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const filterParam = urlParams.get('filter');
    if (filterParam && ['all', 'regular', 'trial', 'vacation'].includes(filterParam)) {
      setFilterStatus(filterParam);
    }
  }, [location]);

  const fetchData = async () => {
    try {
      // Fetch both students and classes
      const [studentsResponse, classesResponse] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/classes`)
      ]);
      
      setStudents(studentsResponse.data);
      setClasses(classesResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = () => {
    setShowAddModal(true);
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    fetchData(); // Refresh both students and classes
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setShowEditModal(true);
  };

  const handleUpdateStudent = async (studentData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/students/${editingStudent.id}`, studentData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setShowEditModal(false);
      fetchStudents(); // Refresh the student list
      return Promise.resolve(); // Success
    } catch (error) {
      console.error('Failed to update student:', error);
      return Promise.reject(error); // Let the modal handle the error
    }
  };

  const handleDeleteStudent = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/students/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setShowEditModal(false);
      fetchStudents(); // Refresh the student list
      return Promise.resolve(); // Success
    } catch (error) {
      console.error('Failed to delete student:', error);
      return Promise.reject(error); // Let the modal handle the error
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'trial' && student.is_trial) ||
                         (filterStatus === 'regular' && !student.is_trial) ||
                         (filterStatus === 'vacation' && student.on_vacation);
    return matchesSearch && matchesStatus && !student.is_instructor;
  });

  // Group students by classes (both enrolled_classes and class_preferences for backward compatibility)
  const studentsByClass = {};
  
  // Initialize class groups based on actual classes
  classes.forEach(classItem => {
    studentsByClass[classItem.id] = {
      classInfo: classItem,
      students: []
    };
  });
  
  // Add an "Unassigned" group for students not enrolled in any class
  studentsByClass['unassigned'] = {
    classInfo: { id: 'unassigned', name: 'Unassigned', description: 'Students not enrolled in any class' },
    students: []
  };

  filteredStudents.forEach(student => {
    let assignedToClass = false;
    
    // Check enrolled_classes first (new system)
    if (student.enrolled_classes && student.enrolled_classes.length > 0) {
      student.enrolled_classes.forEach(classId => {
        if (studentsByClass[classId]) {
          studentsByClass[classId].students.push(student);
          assignedToClass = true;
        }
      });
    } 
    // Fall back to class_preferences (old system) if no enrolled_classes
    else if (student.class_preferences && student.class_preferences.length > 0) {
      // Try to match class_preferences with actual class names
      student.class_preferences.forEach(preference => {
        const matchingClass = classes.find(c => 
          c.name.toLowerCase().includes(preference.toLowerCase()) || 
          preference.toLowerCase().includes(c.name.toLowerCase())
        );
        if (matchingClass && studentsByClass[matchingClass.id]) {
          studentsByClass[matchingClass.id].students.push(student);
          assignedToClass = true;
        }
      });
    }
    
    // If student is not assigned to any class, add to unassigned
    if (!assignedToClass) {
      studentsByClass['unassigned'].students.push(student);
    }
  });

  // Calculate class stats for the overview cards
  const classStats = Object.entries(studentsByClass)
    .filter(([_, classData]) => classData.students.length > 0)
    .map(([classId, classData]) => ({
      classId,
      className: classData.classInfo.name,
      count: classData.students.length,
      classInfo: classData.classInfo
    }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading students...</p>
        </div>
      </div>
    );
  }

  if (showAttendanceSubpage) {
    return <AttendanceManagement onBack={() => setShowAttendanceSubpage(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-8 gap-3 md:gap-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Student Center</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">Manage student information and attendance</p>
          </div>
          
          <div className="grid grid-cols-2 md:flex md:space-x-4 gap-2 md:gap-0">
            <button
              onClick={() => setShowAttendanceSubpage(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 md:px-6 md:py-3 rounded-lg flex items-center justify-center space-x-2 shadow-lg text-sm md:text-base"
              data-testid="student-center-attendance-btn"
            >
              <span>📋</span>
              <span>Attendance</span>
            </button>
            <button
              onClick={handleAddStudent}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 md:px-6 md:py-3 rounded-lg flex items-center justify-center space-x-2 shadow-lg text-sm md:text-base"
              data-testid="student-center-enroll-btn"
            >
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Enroll New Student</span>
            </button>
            {/* Bulk Upload Button - HIDDEN
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              <span>Bulk Upload</span>
            </button>
            */}
          </div>
        </div>

        {/* 📚 Students by Class Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-8">
          <div className="flex items-center mb-4 md:mb-6">
            <div className="bg-purple-100 rounded-lg p-2 md:p-3 mr-3 md:mr-4">
              <span className="text-xl md:text-2xl">📚</span>
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">Students by Class</h2>
              <p className="text-xs md:text-base text-gray-600">Overview of student distribution across different classes</p>
            </div>
          </div>
          
          {classes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No classes created yet. Create classes in Class Center to organize students.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
              {classStats.map(({ classId, className, count, classInfo }) => (
                <div key={classId} className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm md:text-lg font-semibold text-gray-900 truncate">{className}</h3>
                      <p className="text-2xl md:text-3xl font-bold text-purple-600 mt-1 md:mt-2">{count}</p>
                      <p className="text-xs md:text-sm text-gray-600">
                        {count === 1 ? 'student' : 'students'}
                      </p>
                      {classInfo.time && (
                        <p className="text-xs text-gray-500 mt-1 truncate">{classInfo.time}</p>
                      )}
                    </div>
                    <div className="hidden md:block bg-white rounded-full p-3 shrink-0 ml-2">
                      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2.5 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
                data-testid="student-search-input"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
              data-testid="student-filter-select"
            >
              <option value="all">All Students</option>
              <option value="trial">Trial Students</option>
              <option value="regular">Regular Students</option>
              <option value="vacation">Students on Vacation</option>
            </select>
          </div>
        </div>

        {/* Students Organized by Class */}
        <div className="space-y-4 md:space-y-8">
          {Object.entries(studentsByClass)
            .filter(([_, classData]) => classData.students.length > 0)
            .map(([classId, classData]) => {
              const { classInfo, students: classStudents } = classData;
              
              return (
                <div key={classId} className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                  <div className="flex items-center mb-4 md:mb-6">
                    <div className="bg-purple-100 rounded-lg p-2 md:p-3 mr-3 md:mr-4">
                      <svg className="w-5 h-5 md:w-6 md:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-base md:text-xl font-semibold text-gray-900">{classInfo.name}</h2>
                      <p className="text-xs md:text-base text-gray-600">
                        {classStudents.length} student{classStudents.length !== 1 ? 's' : ''}
                        {classInfo.time && ` • ${classInfo.time}`}
                        {classInfo.location && ` • ${classInfo.location}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                    {classStudents.map((student) => (
                      <div key={`${classId}-${student.id}`} className="bg-gray-50 rounded-lg shadow-sm p-4 md:p-6">
                        <div className="flex items-center justify-between mb-3 md:mb-4">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="bg-purple-100 rounded-full p-2 shrink-0">
                              <svg className="w-5 h-5 md:w-6 md:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">{student.name}</h3>
                              <p className="text-xs md:text-sm text-gray-500 truncate">{student.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEdit(student)}
                            className="text-blue-600 hover:text-blue-800 shrink-0 ml-2"
                            data-testid={`edit-student-${student.id}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Belt Level:</span>
                            <span className="text-sm font-medium">{student.belt_level}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Vacation Status:</span>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              student.on_vacation 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {student.on_vacation ? '🏖️ On Vacation' : '✅ Active'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Status:</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              student.is_trial ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {student.is_trial ? 'Trial' : 'Regular'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Phone:</span>
                            <span className="text-sm font-medium">{student.phone_number}</span>
                          </div>
                          <div className="mt-3">
                            <span className="text-sm text-gray-600">Enrolled Classes:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {/* Show enrolled classes (new system) */}
                              {student.enrolled_classes && student.enrolled_classes.length > 0 ? (
                                student.enrolled_classes.map(enrolledClassId => {
                                  const enrolledClass = classes.find(c => c.id === enrolledClassId);
                                  return (
                                    <span 
                                      key={enrolledClassId} 
                                      className={`text-xs px-2 py-1 rounded ${
                                        enrolledClassId === classId 
                                          ? 'bg-purple-200 text-purple-800 font-medium' 
                                          : 'bg-blue-100 text-blue-800'
                                      }`}
                                    >
                                      {enrolledClass ? enrolledClass.name : 'Unknown Class'}
                                    </span>
                                  );
                                })
                              ) : (
                                // Fall back to class preferences (old system)
                                student.class_preferences && student.class_preferences.length > 0 ? (
                                  student.class_preferences.map(pref => (
                                    <span 
                                      key={pref} 
                                      className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-800"
                                    >
                                      {pref.replace('_', ' ')}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                                    No classes assigned
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          }
        </div>

        {filteredStudents.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-500">No students found</p>
          </div>
        )}
      </div>

      {/* Add Student Modal */}
      {showAddModal && (
        <AddStudentModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {/* Edit Student Modal */}
      {showEditModal && editingStudent && (
        <EditStudentModal
          student={editingStudent}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateStudent}
          onDelete={handleDeleteStudent}
          classOptions={classes.map(cls => ({ 
            id: cls.id, 
            name: cls.class_name || cls.name || 'Unnamed Class' 
          }))}
        />
      )}

      {/* Bulk Upload Modal - HIDDEN
      {showBulkUploadModal && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={fetchData}
          classOptions={classes.map(cls => ({ 
            id: cls.id, 
            name: cls.class_name || cls.name || 'Unnamed Class' 
          }))}
        />
      )}
      */}
      
      <NotificationPanel />
    </div>
  );
};

// Edit Student Modal Component
const EditStudentModal = ({ student, isOpen, onClose, onSave, onDelete, classOptions = [] }) => {
  const [formData, setFormData] = useState({
    name: student.name,
    email: student.email,
    phone_number: student.phone_number,
    belt_level: student.belt_level,
    date_of_birth: student.date_of_birth,  // Changed from age to date_of_birth
    gender: student.gender,
    emergency_contact_name: student.emergency_contact_name,
    emergency_contact_phone: student.emergency_contact_phone,
    class_preferences: student.class_preferences || [],
    enrolled_classes: student.enrolled_classes || [],  // Add enrolled classes
    is_trial: student.is_trial,
    monthly_payment_amount: student.monthly_payment_amount || 0,
    on_vacation: student.on_vacation || false,  // Vacation status
    app_returned: student.app_returned || false,  // NEW: APP Retrnd field
    payment_method: student.payment_method || '',  // NEW: Payment Method
    current_month_paid: student.current_month_paid || false  // NEW: Current month paid status
  });
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      // Apply same validation rules as AddStudentModal
      if (name === 'name') {
        // Only letters and spaces allowed for name
        const lettersOnly = value.replace(/[^a-zA-Z\s]/g, '');
        setFormData(prev => ({ ...prev, [name]: lettersOnly }));
      } else if (name === 'phone_number') {
        // Only numbers allowed for phone number (plus sign allowed at start)
        const numbersOnly = value.replace(/[^0-9+\-\s()]/g, '');
        setFormData(prev => ({ ...prev, [name]: numbersOnly }));
      } else if (name === 'date_of_birth') {
        // Date of birth - no special validation needed as HTML date input handles format
        setFormData(prev => ({ ...prev, [name]: value }));
      } else if (name === 'emergency_contact_name') {
        // Only letters and spaces allowed for emergency contact name
        const lettersOnly = value.replace(/[^a-zA-Z\s]/g, '');
        setFormData(prev => ({ ...prev, [name]: lettersOnly }));
      } else if (name === 'emergency_contact_phone') {
        // Only numbers allowed for emergency contact phone
        const numbersOnly = value.replace(/[^0-9+\-\s()]/g, '');
        setFormData(prev => ({ ...prev, [name]: numbersOnly }));
      } else if (name === 'monthly_payment_amount') {
        // Only numbers and decimal point allowed for payment amount
        const numbersOnly = value.replace(/[^0-9.]/g, '');
        // Ensure only one decimal point
        const parts = numbersOnly.split('.');
        const sanitizedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numbersOnly;
        setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleClassPreferenceChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      class_preferences: checked
        ? [...prev.class_preferences, value]
        : prev.class_preferences.filter(pref => pref !== value)
    }));
  };

  const handleClassEnrollmentChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      enrolled_classes: checked
        ? [...prev.enrolled_classes, value]
        : prev.enrolled_classes.filter(classId => classId !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Validation: Check if at least one class is selected
    if (formData.enrolled_classes.length === 0) {
      alert('Student must be enrolled in at least one class.');
      setSaving(false);
      return;
    }
    
    try {
      await onSave({
        ...formData
        // No need to parseInt date_of_birth as it's already a string
      });
      
      setStudentName(formData.name);
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to update student:', error);
      alert('Failed to update student. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose(); // This will close the edit modal and stay on student management
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await onDelete(student.id);
      setShowDeleteConfirm(false);
      onClose(); // Close the modal after successful deletion
    } catch (error) {
      console.error('Failed to delete student:', error);
      alert('Failed to delete student. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-md max-h-96 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Student</h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]} // Prevent future dates
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Belt Level</label>
                  <select
                    name="belt_level"
                    value={formData.belt_level}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="white">White</option>
                    <option value="yellow">Yellow</option>
                    <option value="orange">Orange</option>
                    <option value="green">Green</option>
                    <option value="blue">Blue</option>
                    <option value="brown">Brown</option>
                    <option value="black">Black</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Payment Amount ($)</label>
                  <input
                    type="number"
                    name="monthly_payment_amount"
                    value={formData.monthly_payment_amount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name</label>
                <input
                  type="text"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone</label>
                <input
                  type="tel"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Class Preferences</label>
                <div className="space-y-2">
                  {classOptions.length > 0 ? classOptions.map(classOption => (
                    <label key={classOption.id} className="flex items-center">
                      <input
                        type="checkbox"
                        value={classOption.id}
                        checked={formData.class_preferences.includes(classOption.id)}
                        onChange={handleClassPreferenceChange}
                        className="mr-2"
                      />
                      <span className="text-sm">{classOption.name}</span>
                    </label>
                  )) : (
                    <p className="text-sm text-gray-500 italic">No classes available. Create classes in Class Center first.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class Enrollment * 
                  <span className="text-xs text-gray-500 ml-1">(Select at least one)</span>
                </label>
                {classOptions.length === 0 ? (
                  <div className="p-3 border border-gray-300 rounded-md">
                    <p className="text-sm text-gray-500">No classes available. Please create classes in Class Center first.</p>
                  </div>
                ) : (
                  <div className={`space-y-2 p-3 border rounded-md ${
                    formData.enrolled_classes.length === 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}>
                    {classOptions.map(classItem => (
                      <label key={classItem.id} className="flex items-center">
                        <input
                          type="checkbox"
                          value={classItem.id}
                          checked={formData.enrolled_classes.includes(classItem.id)}
                          onChange={handleClassEnrollmentChange}
                          className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-900">{classItem.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_trial"
                  checked={formData.is_trial}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">Trial Student</label>
              </div>

              {/* Vacation Status Toggle */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">🏖️</span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <label className="text-sm font-medium text-gray-900">Vacation Status</label>
                      <p className="text-xs text-gray-600">
                        Students on vacation will not receive any WhatsApp messages
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="on_vacation"
                          checked={formData.on_vacation}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`block w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                          formData.on_vacation 
                            ? 'bg-orange-500' 
                            : 'bg-gray-300'
                        }`}>
                          <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                            formData.on_vacation 
                              ? 'transform translate-x-6' 
                              : ''
                          }`}></div>
                        </div>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {formData.on_vacation ? 'On Vacation' : 'Active'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                
                {/* Delete Button */}
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors duration-200 font-medium"
                >
                  Delete Student
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccess}
        title="Student Profile Updated!"
        message={`${studentName}'s profile has been successfully updated. All changes have been saved.`}
        onClose={handleSuccessClose}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Student</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>{student.name}</strong>? This will permanently remove their profile, attendance records, payment history, and all associated data from the system.
            </p>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Student'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Bulk Upload Modal Component
const BulkUploadModal = ({ isOpen, onClose, onSuccess, classOptions = [] }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Instructions, 2: Upload, 3: Results
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'sheets'
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [loading, setLoading] = useState(false); // NEW: For file processing
  const [error, setError] = useState(''); // NEW: For error display
  
  // Google Sheets specific states
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetName, setSheetName] = useState('Sheet1');
  const [sheetValidating, setSheetValidating] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const [sheetHeaders, setSheetHeaders] = useState([]);

  // Validate and sanitize classOptions prop
  const safeClassOptions = React.useMemo(() => {
    try {
      if (!classOptions || !Array.isArray(classOptions)) {
        console.warn('Invalid classOptions prop, using empty array');
        return [];
      }
      
      return classOptions.filter(cls => {
        if (!cls || typeof cls !== 'object') {
          console.warn('Invalid class object:', cls);
          return false;
        }
        
        if (!cls.id || (!cls.name && !cls.class_name)) {
          console.warn('Class missing required fields (id, name):', cls);
          return false;
        }
        
        return true;
      });
    } catch (error) {
      console.error('Error processing classOptions:', error);
      return [];
    }
  }, [classOptions]);

  const generateTemplate = () => {
    try {
      // Get class names safely
      const getClassNames = () => {
        if (safeClassOptions.length === 0) {
          return 'Cedar Park, Travisso'; // fallback
        }
        
        return safeClassOptions
          .map(c => c.name || c.class_name || 'Unnamed Class')
          .join(', ');
      };
      
      // Create template data with headers and sample rows (ASKA format)
      const templateData = [
        {
          'Full Name': 'Doe, John',  // Last, First format
          'APP Retrnd': 'Yes',       // APP Returned field
          'Fee': '150.0',            // Monthly fee amount
          'Paid': 'Yes',             // Current month paid status
          'Payment Method': 'Zelle',  // Payment method
          'Email': 'john.doe@example.com',
          'Phone Number': '(555) 123-4567',
          'Date of Birth': '2010-06-15',
          'Gender': 'Male',
          'Belt Level': 'White',
          'Emergency Contact Name': 'Jane Doe',
          'Emergency Contact Phone': '(555) 987-6543',
          'Class Preferences': 'LAKELINE, CEDAR PARK',
          'Is Trial Student': 'FALSE'
        },
        {
          'Full Name': 'Smith, Christina',
          'APP Retrnd': 'YES',
          'Fee': 'INSTRUCTOR',       // Special case for instructors
          'Paid': 'YES',
          'Payment Method': 'PayPal',
          'Email': 'christina.smith@example.com',
          'Phone Number': '(512) 484-4020',
          'Date of Birth': '1980-12-31',
          'Gender': 'Female',
          'Belt Level': 'Black',
          'Emergency Contact Name': 'ADULT',  // Adult student example
          'Emergency Contact Phone': '(512) 484-4020',
          'Class Preferences': 'LAKELINE, CEDAR PARK',
          'Is Trial Student': 'False'
        },
        {
          'Full Name': 'Johnson, Jacob',
          'APP Retrnd': 'YES',
          'Fee': '125.0',
          'Paid': 'YES',
          'Payment Method': 'Check',
          'Email': 'johnson.family@example.com',
          'Phone Number': '(661) 747-5524',
          'Date of Birth': '2008-04-29',
          'Gender': 'Male',
          'Belt Level': 'Brown',
          'Emergency Contact Name': 'SARAH & MIKE',
          'Emergency Contact Phone': '(661) 747-5524',
          'Class Preferences': 'LAKELINE',
          'Is Trial Student': 'False'
        },
        {
          'Full Name': 'Davis, Emily',
          'APP Retrnd': 'YES',
          'Fee': '130.0',
          'Paid': 'NO',             // Example of unpaid student
          'Payment Method': 'PayPal',
          'Email': 'davis.family@example.com',
          'Phone Number': '(787) 562-0902',
          'Date of Birth': '2011-11-06',
          'Gender': 'Female',
          'Belt Level': 'Orange',
          'Emergency Contact Name': 'MARIA',
          'Emergency Contact Phone': '(787) 562-0902',
          'Class Preferences': 'CEDAR PARK',
          'Is Trial Student': 'True'   // Trial student example
        }
      ];

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(templateData);

      // Set column widths (ASKA format)
      const colWidths = [
        { wch: 22 },   // Full Name
        { wch: 17 },   // APP Retrnd
        { wch: 13 },   // Fee
        { wch: 11 },   // Paid
        { wch: 21 },   // Payment Method
        { wch: 23 },   // Email
        { wch: 17 },   // Phone Number
        { wch: 18 },   // Date of Birth
        { wch: 13 },   // Gender
        { wch: 15 },   // Belt Level
        { wch: 25 },   // Emergency Contact Name
        { wch: 26 },   // Emergency Contact Phone
        { wch: 33 },   // Class Preferences
        { wch: 20 }    // Is Trial Student
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'StudentTemplate');

      // Generate and download file
      XLSX.writeFile(wb, 'student_bulk_upload_template.xlsx');
    } catch (error) {
      console.error('Error generating template:', error);
      alert('Error generating template. Please try again.');
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseExcelFile(selectedFile);
    }
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        
        // Validate that we have data
        if (!data) {
          throw new Error('File is empty or could not be read');
        }
        
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // Validate workbook
        if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
          throw new Error('Invalid Excel file - no sheets found');
        }
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Validate worksheet
        if (!worksheet) {
          throw new Error('Could not read the first worksheet');
        }
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Validate that we have data rows
        if (!jsonData || jsonData.length === 0) {
          throw new Error('No data found in Excel file. Please make sure your file has data rows.');
        }
        
        // Check for required headers
        const sampleRow = jsonData[0];
        const requiredHeaders = ['Full Name', 'Email', 'Phone Number'];
        const missingHeaders = requiredHeaders.filter(header => !(header in sampleRow));
        
        if (missingHeaders.length > 0) {
          throw new Error(`Missing required columns: ${missingHeaders.join(', ')}. Please download the template and use the correct format.`);
        }
        
        // Helper function to format date from Excel
        const formatExcelDate = (excelDate) => {
          try {
            if (!excelDate) return '';
            
            // Handle null/undefined/empty values
            if (excelDate === null || excelDate === undefined || excelDate === '') {
              return '';
            }
            
            console.log('Processing date value:', excelDate, typeof excelDate);
            
            // If it's already in YYYY-MM-DD format, return as-is
            if (typeof excelDate === 'string') {
              const trimmed = excelDate.trim();
              
              // Handle datetime strings like "2008-04-29 00:00:00"
              if (trimmed.includes(' ')) {
                const datePart = trimmed.split(' ')[0];
                if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                  return datePart;
                }
              }
              
              // Handle YYYY-MM-DD format
              if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                return trimmed;
              }
              
              // Try to parse other string formats
              const parsedDate = new Date(trimmed);
              if (!isNaN(parsedDate.getTime())) {
                return parsedDate.toISOString().split('T')[0];
              }
            }
            
            // If it's a Date object
            if (excelDate instanceof Date) {
              return excelDate.toISOString().split('T')[0]; // Returns YYYY-MM-DD
            }
            
            // If it's an Excel serial number (number)
            if (typeof excelDate === 'number') {
              const excelEpoch = new Date(1900, 0, 1); // Excel epoch (Jan 1, 1900)
              const date = new Date(excelEpoch.getTime() + (excelDate - 1) * 24 * 60 * 60 * 1000);
              return date.toISOString().split('T')[0];
            }
            
            console.warn('Could not parse date value:', excelDate);
            return '';
          } catch (error) {
            console.warn('Error formatting date:', excelDate, error);
            return '';
          }
        };
        
        // Map the Excel data to our student format with comprehensive error handling
        const mappedData = [];
        
        for (let index = 0; index < jsonData.length; index++) {
          try {
            const row = jsonData[index];
            console.log(`Processing row ${index + 1}:`, row);
            
            // Helper function to safely parse payment amount
            const parsePaymentAmount = (value) => {
              try {
                if (!value) return 100; // default value
                if (typeof value === 'number') return value;
                if (typeof value === 'string') {
                  const cleaned = value.replace(/[$,]/g, ''); // Remove $ and commas
                  const parsed = parseFloat(cleaned);
                  return isNaN(parsed) ? 100 : parsed;
                }
                return 100;
              } catch (error) {
                console.warn('Error parsing payment amount:', value, error);
                return 100;
              }
            };

            // Helper function to transform name from "Last, First" to "First Last"
            const transformName = (fullName) => {
              try {
                if (!fullName || typeof fullName !== 'string') return '';
                const trimmed = fullName.trim();
                
                // Check if name is in "Last, First" format
                if (trimmed.includes(',')) {
                  const parts = trimmed.split(',');
                  if (parts.length >= 2) {
                    const lastName = parts[0].trim();
                    const firstName = parts[1].trim();
                    return `${firstName} ${lastName}`;
                  }
                }
                
                // If not in comma format, return as-is
                return trimmed;
              } catch (error) {
                console.warn('Error transforming name:', fullName, error);
                return fullName || '';
              }
            };

            // Helper function to handle emergency contact
            const processEmergencyContact = (contactName, contactPhone) => {
              try {
                const name = (contactName || '').toString().trim();
                const phone = (contactPhone || '').toString().trim();
                
                // If emergency contact says "ADULT", mark as no emergency contact
                if (name.toUpperCase() === 'ADULT') {
                  return {
                    emergency_contact_name: 'No Emergency Contact',
                    emergency_contact_phone: phone || 'N/A'
                  };
                }
                
                return {
                  emergency_contact_name: name,
                  emergency_contact_phone: phone
                };
              } catch (error) {
                console.warn('Error processing emergency contact:', contactName, contactPhone, error);
                return {
                  emergency_contact_name: 'Unknown',
                  emergency_contact_phone: 'N/A'
                };
              }
            };

            // Helper function to parse boolean values from spreadsheet
            const parseBoolean = (value) => {
              try {
                if (typeof value === 'boolean') return value;
                if (typeof value === 'string') {
                  const str = value.toString().toLowerCase().trim();
                  return str === 'yes' || str === 'true' || str === '1';
                }
                return false;
              } catch (error) {
                console.warn('Error parsing boolean:', value, error);
                return false;
              }
            };

            // Safe field extraction with defaults
            const studentData = {
              rowNumber: index + 2, // Excel row number (header is row 1)
              name: transformName(row['Full Name']),
              email: (row['Email'] || '').toString().trim(),
              phone_number: (row['Phone Number'] || '').toString().trim(),
              date_of_birth: formatExcelDate(row['Date of Birth']),
              gender: (row['Gender'] || '').toString().trim(),
              belt_level: (row['Belt Level'] || 'White').toString().trim(),
              ...processEmergencyContact(row['Emergency Contact Name'], row['Emergency Contact Phone']),
              class_preferences_text: (row['Class Preferences'] || '').toString().trim(),
              is_trial: parseBoolean(row['Is Trial Student']),
              monthly_payment_amount: parsePaymentAmount(row['Fee']), // Updated to use 'Fee' column
              app_returned: parseBoolean(row['APP Retrnd']), // NEW: APP Retrnd field
              payment_method: (row['Payment Method'] || '').toString().trim(), // NEW: Payment Method
              current_month_paid: parseBoolean(row['Paid']) // NEW: Current month paid status
            };
            
            console.log(`Successfully processed row ${index + 1}:`, studentData);
            mappedData.push(studentData);
            
          } catch (error) {
            console.error(`Critical error processing row ${index + 2}:`, error);
            
            // Don't crash the entire app - skip this row and continue
            console.warn(`Skipping row ${index + 2} due to processing error`);
            
            // Optional: Add this row to a separate errors array for user feedback
            continue;
          }
        }

        console.log(`Successfully parsed ${mappedData.length} student records from Excel file`);
        setParsedData(mappedData);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        
        console.error('CRITICAL ERROR in Excel file processing:', error);
        console.error('Error stack:', error.stack);
        
        // Provide more specific error messages
        let errorMessage = 'Error parsing Excel file: ';
        if (error.message) {
          errorMessage += error.message;
        } else {
          errorMessage += 'Please make sure you are using the correct template format.';
        }
        
        // Don't use alert() - use state management instead
        setError(errorMessage);
        setLoading(false);
        
        // Reset state on error
        setParsedData([]);
        setFile(null);
      }
    };
    
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      setError('Error reading file. Please try again with a different file.');
      setLoading(false);
      setParsedData([]);
      setFile(null);
    };
    
    reader.readAsBinaryString(file);
  };

  const validateStudentData = (student) => {
    const errors = [];
    
    if (!student.name?.trim()) {
      errors.push('Full Name is required');
    }
    
    if (!student.email?.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(student.email)) {
      errors.push('Invalid email format');
    }
    
    if (!student.phone_number?.trim()) {
      errors.push('Phone Number is required');
    }
    
    // Enhanced date validation with better error messages
    if (student.date_of_birth) {
      const dateStr = student.date_of_birth.trim();
      
      // Check if it matches YYYY-MM-DD format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        errors.push(`Date of Birth must be in YYYY-MM-DD format. Found: "${dateStr}"`);
      } else {
        // Validate if it's a real date
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
          errors.push(`Invalid date: "${dateStr}". Please check the day, month, and year values.`);
        }
        
        // Check if date is reasonable (not in the future, not too old)
        const today = new Date();
        const maxAge = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
        
        if (date > today) {
          errors.push(`Date of Birth cannot be in the future: "${dateStr}"`);
        } else if (date < maxAge) {
          errors.push(`Date of Birth seems too old: "${dateStr}"`);
        }
      }
    }
    
    return errors;
  };

  const mapClassNames = (classPreferencesText) => {
    try {
      // Validate inputs
      if (!classPreferencesText || typeof classPreferencesText !== 'string') {
        console.warn('Invalid class preferences text:', classPreferencesText);
        return [];
      }
      
      // Use safeClassOptions instead of classOptions
      if (!safeClassOptions || !Array.isArray(safeClassOptions) || safeClassOptions.length === 0) {
        console.warn('No valid class options available');
        return [];
      }
      
      const classNames = classPreferencesText.split(',').map(name => name.trim()).filter(name => name.length > 0);
      const classIds = [];
      
      classNames.forEach(className => {
        try {
          const matchedClass = safeClassOptions.find(cls => {
            // Handle both new class_name and legacy name fields with safe access
            const classDisplayName = (cls.name || cls.class_name || '').toString();
            return classDisplayName.toLowerCase() === className.toLowerCase();
          });
          
          if (matchedClass && matchedClass.id) {
            classIds.push(matchedClass.id);
          } else {
            console.warn(`No matching class found for: "${className}"`);
          }
        } catch (error) {
          console.error(`Error processing class name "${className}":`, error);
        }
      });
      
      console.log(`Mapped ${classNames.length} class names to ${classIds.length} class IDs`);
      return classIds;
    } catch (error) {
      console.error('Error in mapClassNames:', error);
      return [];
    }
  };

  const handleUpload = async () => {
    try {
      if (!parsedData.length) {
        setError('Please select a file with student data first.');
        return;
      }

      setUploading(true);
      setError('');
      
      console.log(`Starting batch upload of ${parsedData.length} students...`);
      
      // Prepare all student data for batch API call
      const studentsPayload = [];
      const validationResults = {
        successful: [],
        failed: []
      };
      
      // Validate and prepare all students first
      for (const studentData of parsedData) {
        try {
          const validationErrors = validateStudentData(studentData);
          
          if (validationErrors.length > 0) {
            validationResults.failed.push({
              row: studentData.rowNumber,
              data: studentData,
              errors: validationErrors
            });
            continue;
          }

          // Map class preferences to valid class options
          const mapClassNames = (classPreferencesText) => {
            if (!classPreferencesText) return ['cedar_park']; // Default value
            
            const preferences = classPreferencesText.toLowerCase().split(',').map(p => p.trim());
            const validClasses = [];
            
            preferences.forEach(pref => {
              if (pref.includes('cedar') || pref.includes('lakeline')) {
                validClasses.push('cedar_park');
              } else if (pref.includes('travisso')) {
                validClasses.push('travisso');
              } else if (pref.includes('saturday')) {
                validClasses.push('saturday');
              }
            });
            
            return validClasses.length > 0 ? validClasses : ['cedar_park'];
          };
          
          const studentPayload = {
            name: studentData.name,
            email: studentData.email,
            password: 'BulkUpload123!', // Default password for bulk uploads
            phone_number: studentData.phone_number,
            date_of_birth: studentData.date_of_birth || '2000-01-01',
            gender: studentData.gender,
            belt_level: studentData.belt_level,
            emergency_contact_name: studentData.emergency_contact_name,
            emergency_contact_phone: studentData.emergency_contact_phone,
            class_preferences: mapClassNames(studentData.class_preferences_text), // Use the mapped class names
            enrolled_classes: [], // Initialize as empty, we'll enroll separately if needed
            is_trial: studentData.is_trial,
            monthly_payment_amount: studentData.monthly_payment_amount,
            app_returned: studentData.app_returned || false, // NEW field
            payment_method: studentData.payment_method || '', // NEW field  
            current_month_paid: studentData.current_month_paid || false // NEW field
          };

          studentsPayload.push(studentPayload);
          validationResults.successful.push({
            row: studentData.rowNumber,
            name: studentData.name,
            email: studentData.email
          });

        } catch (error) {
          console.error(`Error preparing student data for row ${studentData.rowNumber}:`, error);
          validationResults.failed.push({
            row: studentData.rowNumber,
            data: studentData,
            errors: [`Processing error: ${error.message}`]
          });
        }
      }

      console.log(`Prepared ${studentsPayload.length} students for batch upload`);
      
      // If no valid students, show validation results and stop
      if (studentsPayload.length === 0) {
        setUploadResults({
          successful: [],
          failed: validationResults.failed,
          total_processed: parsedData.length,
          success_count: 0,
          failure_count: validationResults.failed.length
        });
        setUploading(false);
        return;
      }

      // Make single batch API call instead of multiple individual calls
      console.log('Making batch API call...');
      const startTime = Date.now();
      
      const response = await axios.post(`${API}/students/batch`, studentsPayload, {
        timeout: 120000 // 2 minute timeout for large batches
      });
      
      const endTime = Date.now();
      console.log(`Batch upload completed in ${(endTime - startTime) / 1000} seconds`);

      if (response.data && response.data.results) {
        // Combine validation errors with API results
        const finalResults = {
          successful: [...validationResults.successful.slice(0, response.data.results.success_count), ...response.data.results.successful],
          failed: [...validationResults.failed, ...response.data.results.failed],
          total_processed: parsedData.length,
          success_count: response.data.results.success_count,
          failure_count: validationResults.failed.length + response.data.results.failure_count,
          whatsapp_messages_sent: response.data.whatsapp_messages_sent || 0
        };

        setUploadResults(finalResults);
        
        // Success callback
        if (onSuccess && finalResults.success_count > 0) {
          onSuccess();
        }
        
        console.log(`Batch upload results: ${finalResults.success_count} successful, ${finalResults.failure_count} failed`);
      } else {
        throw new Error('Invalid response from batch upload API');
      }

    } catch (error) {
      console.error('Batch upload error:', error);
      let errorMessage = 'Error during batch upload: ';
      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check your data and try again.';
      }
      
      setError(errorMessage);
      
      // Set partial results if available
      if (error.response?.data?.results) {
        setUploadResults(error.response.data.results);
      }
      
    } finally {
      setUploading(false);
    }
  };

  // Google Sheets Functions
  const validateSheetUrl = async () => {
    if (!sheetUrl.trim()) {
      setSheetError('Please enter a Google Sheets URL');
      return;
    }

    setSheetValidating(true);
    setSheetError('');

    try {
      const response = await axios.post(`${API}/sheets/validate-url`, {
        sheet_url: sheetUrl,
        sheet_name: sheetName
      });

      if (response.data.accessible) {
        setSheetHeaders(response.data.headers);
        setSheetError('');
        alert('✅ Sheet is accessible! You can now load the data.');
      } else {
        setSheetError(response.data.error || 'Unable to access the sheet. Make sure it\'s publicly accessible.');
      }
    } catch (error) {
      console.error('Error validating sheet:', error);
      setSheetError(error.response?.data?.detail || 'Error validating sheet URL. Make sure the sheet is publicly accessible.');
    } finally {
      setSheetValidating(false);
    }
  };

  const loadSheetData = async () => {
    if (!sheetUrl.trim()) {
      setSheetError('Please enter a Google Sheets URL');
      return;
    }

    setUploading(true);
    setSheetError('');

    try {
      const response = await axios.post(`${API}/sheets/read-public`, {
        sheet_url: sheetUrl,
        sheet_name: sheetName
      });

      if (response.data.success) {
        setParsedData(response.data.data);
        setCurrentStep(2); // Move to upload step to show preview and upload
        alert(`✅ Successfully loaded ${response.data.count} records from Google Sheets!`);
      } else {
        setSheetError('Failed to load sheet data');
      }
    } catch (error) {
      console.error('Error loading sheet:', error);
      setSheetError(error.response?.data?.detail || 'Error loading sheet data. Please check the URL and try again.');
    } finally {
      setUploading(false);
    }
  };

  // Function to create Google Sheets template for instructor
  const createGoogleSheetsTemplate = async () => {
    try {
      setSheetValidating(true);
      
      // Call backend to create template structure
      const response = await axios.post(`${API}/sheets/create-template`);
      
      if (response.data.success) {
        const { template_url, template_data, instructions } = response.data;
        
        // Open the Google Sheets creation URL
        window.open(template_url, '_blank');
        
        // Show structured instructions
        setTimeout(() => {
          alert(instructions);
        }, 1000);
        
        // Optionally, show additional context in the UI
        setSheetError('');
        console.log('Template data structure:', template_data);
        
      } else {
        setSheetError('Failed to create template structure. Please try again.');
      }
      
    } catch (error) {
      console.error('Error creating template:', error);
      setSheetError('Error creating template. Please create manually at sheets.google.com');
      
      // Fallback: open Google Sheets directly
      window.open('https://docs.google.com/spreadsheets/create', '_blank');
      
      // Show fallback instructions
      setTimeout(() => {
        alert(`📊 Manual Template Setup Required:

🔥 HEADERS (Row 1):
Full Name | APP Retrnd | Fee | Paid | Payment Method | Email | Phone Number | Date of Birth | Gender | Belt Level | Emergency Contact Name | Emergency Contact Phone | Class Preferences | Is Trial Student

📝 SAMPLE DATA (Row 2):
Johnson, Sarah | YES | 125.0 | YES | Zelle | sarah.johnson@example.com | (555) 123-4567 | 2010-06-15 | Female | White | PARENT JOHNSON | (555) 123-4567 | LAKELINE, CEDAR PARK | FALSE

📝 ADULT STUDENT EXAMPLE (Row 3):
Williams, Michael | YES | INSTRUCTOR | YES | PayPal | michael.williams@example.com | (512) 484-4020 | 1985-03-15 | Male | Black | ADULT | (512) 484-4020 | LAKELINE | False

🔧 SETUP STEPS:
1. Copy headers to Row 1
2. Add sample data to Row 2  
3. Share → Anyone with link can view
4. Copy URL and paste below`);
      }, 1000);
      
    } finally {
      setSheetValidating(false);
    }
  };

  const resetModal = () => {
    setCurrentStep(1);
    setUploadMethod('file');
    setFile(null);
    setParsedData([]);
    setUploadResults(null);
    // Reset Google Sheets states
    setSheetUrl('');
    setSheetName('Sheet1');
    setSheetValidating(false);
    setSheetError('');
    setSheetHeaders([]);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Bulk Upload Students</h3>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center mb-8">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="ml-2 font-medium">Instructions</span>
            </div>
            <div className="flex-1 h-px bg-gray-300 mx-4"></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Upload</span>
            </div>
            <div className="flex-1 h-px bg-gray-300 mx-4"></div>
            <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className="ml-2 font-medium">Results</span>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-red-800">Processing Error</h4>
                  <div className="mt-1 text-sm text-red-700 whitespace-pre-line">
                    {error}
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={() => setError('')}
                      className="text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded-md border border-red-300"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading Display */}
          {loading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="animate-spin w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-800">Processing Excel file...</p>
                  <p className="text-sm text-blue-600">Please wait while we parse your data.</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Instructions */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Upload Method Selector */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Choose Upload Method:</h4>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setUploadMethod('file')}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                      uploadMethod === 'file'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    📄 Excel File Upload
                  </button>
                  <button
                    onClick={() => setUploadMethod('sheets')}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                      uploadMethod === 'sheets'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    📊 Google Sheets
                  </button>
                </div>
              </div>

              {/* File Upload Instructions */}
              {uploadMethod === 'file' && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">📋 Step-by-Step Instructions:</h4>
                    <ol className="list-decimal list-inside space-y-2 text-blue-800">
                      <li><strong>Download the template</strong> by clicking the button below</li>
                      <li><strong>Open the template</strong> in Excel or Google Sheets</li>
                      <li><strong>Fill out student information</strong> in each row (remove the sample row)</li>
                      <li><strong>Save as Excel file</strong> (.xlsx format)</li>
                      <li><strong>Upload the completed file</strong> using the upload button</li>
                    </ol>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-3">⚠️ Important Notes:</h4>
                    <ul className="list-disc list-inside space-y-1 text-yellow-800 text-sm">
                      <li><strong>Email addresses must be unique</strong> - duplicate emails will be rejected</li>
                      <li><strong>Date format:</strong> Use YYYY-MM-DD (e.g., 2010-06-15)</li>
                      <li><strong>Class Preferences:</strong> Use exact class names separated by commas</li>
                      <li><strong>Available classes:</strong> {classOptions.map(c => c.name).join(', ') || 'No classes created yet'}</li>
                      <li><strong>Is Trial Student:</strong> Use TRUE or FALSE</li>
                      <li><strong>Phone numbers:</strong> Include area code in parentheses (e.g., (555) 123-4567)</li>
                    </ul>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={generateTemplate}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download Template</span>
                    </button>
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
                    >
                      Next: Upload File
                    </button>
                  </div>
                </>
              )}

              {/* Google Sheets Instructions */}
              {uploadMethod === 'sheets' && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-3">📊 Easy Google Sheets Upload:</h4>
                    <ol className="list-decimal list-inside space-y-2 text-green-800">
                      <li><strong>Copy our preset template:</strong> Click the template link below to copy our ready-to-use Google Sheets template</li>
                      <li><strong>Fill in your data:</strong> Replace the sample data with your actual student information</li>
                      <li><strong>Make it public:</strong> Click "Share" → "Change to anyone with the link can view"</li>
                      <li><strong>Use your sheet URL:</strong> Copy your sheet URL and paste it below to upload</li>
                    </ol>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">🎯 Preset Template (Copy This Sheet):</h4>
                    <div className="flex items-center justify-between bg-white border rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19,3H5C3.9,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.9 20.1,3 19,3M19,5V7H17V5H19M15,5V7H9V5H15M7,5V7H5V5H7M5,9H7V15H5V9M9,9H15V15H9V9M17,9H19V15H17V9M5,17V19H7V17H5M9,17H15V19H9V17M17,17V19H19V17H17Z" />
                        </svg>
                        <div>
                          <p className="font-medium text-gray-900">Student Bulk Upload Template</p>
                          <p className="text-sm text-gray-600">Pre-configured with all required columns and sample data</p>
                        </div>
                      </div>
                      <button
                        onClick={createGoogleSheetsTemplate}
                        disabled={sheetValidating}
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2"
                      >
                        {sheetValidating ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Creating...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M19,3H5C3.9,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.9 20.1,3 19,3M19,5V7H17V5H19M15,5V7H9V5H15M7,5V7H5V5H7M5,9H7V15H5V9M9,9H15V15H9V9M17,9H19V15H17V9M5,17V19H7V17H5M9,17H15V19H9V17M17,17V19H19V17H17Z" />
                            </svg>
                            <span>Create Template</span>
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      ⚡ This will guide you through creating a properly formatted Google Sheets template with all required headers.
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">📋 Column Headers in Template:</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                      <span>• Full Name</span>
                      <span>• Email</span>
                      <span>• Phone Number</span>
                      <span>• Date of Birth</span>
                      <span>• Gender</span>
                      <span>• Belt Level</span>
                      <span>• Emergency Contact Name</span>
                      <span>• Emergency Contact Phone</span>
                      <span>• Class Preferences</span>
                      <span>• Is Trial Student</span>
                      <span>• Monthly Payment Amount</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Google Sheets URL *
                      </label>
                      <input
                        type="url"
                        value={sheetUrl}
                        onChange={(e) => setSheetUrl(e.target.value)}
                        placeholder="https://docs.google.com/spreadsheets/d/your-sheet-id/edit..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Paste the URL of your copy of the template after filling it with student data
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sheet Name (if different from "Sheet1")
                      </label>
                      <input
                        type="text"
                        value={sheetName}
                        onChange={(e) => setSheetName(e.target.value)}
                        placeholder="Sheet1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {sheetError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-red-700 text-sm">{sheetError}</p>
                      </div>
                    )}

                    {sheetHeaders.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-blue-700 text-sm font-medium mb-2">Sheet Headers Found:</p>
                        <p className="text-blue-600 text-sm">{sheetHeaders.join(', ')}</p>
                      </div>
                    )}

                    <div className="flex space-x-3">
                      <button
                        onClick={validateSheetUrl}
                        disabled={!sheetUrl.trim() || sheetValidating}
                        className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                      >
                        {sheetValidating ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Validating...</span>
                          </>
                        ) : (
                          <span>Validate URL</span>
                        )}
                      </button>
                      
                      <button
                        onClick={loadSheetData}
                        disabled={!sheetUrl.trim() || uploading}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                      >
                        {uploading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Loading...</span>
                          </>
                        ) : (
                          <span>Load Data</span>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Upload */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* File Upload Section */}
              {uploadMethod === 'file' && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <div className="space-y-4">
                    <div className="text-6xl">📄</div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Your Excel File</h4>
                      <p className="text-gray-600 mb-4">Select the completed student template file</p>
                    </div>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                      id="bulk-upload-file"
                    />
                    <label
                      htmlFor="bulk-upload-file"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg cursor-pointer inline-flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      <span>Choose File</span>
                    </label>
                    {file && (
                      <p className="text-green-600 font-medium">✅ {file.name}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Google Sheets Data Loaded Section */}
              {uploadMethod === 'sheets' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="space-y-4">
                    <div className="text-6xl">📊</div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-900 mb-2">Google Sheets Data Loaded</h4>
                      <p className="text-green-600 mb-2">Successfully loaded {parsedData.length} student records</p>
                      <p className="text-green-600 text-sm">From: {sheetUrl}</p>
                    </div>
                  </div>
                </div>
              )}

              {parsedData.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Preview: {parsedData.length} students found</h4>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left p-2">Row</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Classes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.slice(0, 10).map((student, index) => (
                          <tr key={index} className="border-b">
                            <td className="p-2">{student.rowNumber}</td>
                            <td className="p-2">{student.name}</td>
                            <td className="p-2">{student.email}</td>
                            <td className="p-2">{student.class_preferences_text}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedData.length > 10 && (
                      <p className="text-gray-600 text-center p-2">... and {parsedData.length - 10} more students</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg"
                >
                  Back
                </button>
                <button
                  onClick={handleUpload}
                  disabled={(uploadMethod === 'file' && (!file || parsedData.length === 0)) || (uploadMethod === 'sheets' && parsedData.length === 0) || uploading}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg flex items-center space-x-2"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      <span>Upload Students</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {currentStep === 3 && uploadResults && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">✅ Successful Uploads</h4>
                  <p className="text-2xl font-bold text-green-600">{uploadResults.successful.length}</p>
                  <p className="text-green-700 text-sm">students added successfully</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2">❌ Failed Uploads</h4>
                  <p className="text-2xl font-bold text-red-600">{uploadResults.failed.length}</p>
                  <p className="text-red-700 text-sm">students with errors</p>
                </div>
              </div>

              {uploadResults.failed.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-3">Errors to Fix:</h4>
                  <div className="max-h-64 overflow-y-auto space-y-3">
                    {uploadResults.failed.map((failure, index) => (
                      <div key={index} className="bg-white border border-red-200 rounded p-3">
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="font-medium">Row {failure.row}: {failure.data.name}</h5>
                          <span className="text-sm text-gray-600">{failure.data.email}</span>
                        </div>
                        <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                          {failure.errors.map((error, errorIndex) => (
                            <li key={errorIndex}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={resetModal}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
                >
                  Upload Another File
                </button>
                <button
                  onClick={handleClose}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Attendance Management Component
const AttendanceManagement = ({ onBack }) => {
  const { user } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClass, setSelectedClass] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [filterDate, setFilterDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [axios.get(`${API}/attendance`)];
      
      if (user.is_instructor) {
        promises.push(
          axios.get(`${API}/students`),
          axios.get(`${API}/classes`)
        );
      }
      
      const responses = await Promise.all(promises);
      
      setAttendance(responses[0].data);
      
      if (user.is_instructor && responses.length > 1) {
        setStudents(responses[1].data.filter(s => !s.is_instructor));
        setClasses(responses[2].data || []);
        
        // Set default selected class to first available class
        if (responses[2].data && responses[2].data.length > 0) {
          setSelectedClass(responses[2].data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAttendance = async (studentId, present) => {
    try {
      const selectedClassInfo = classes.find(c => c.id === selectedClass);
      
      await axios.post(`${API}/attendance`, {
        student_id: studentId,
        class_id: selectedClass,
        class_date: new Date(selectedDate).toISOString(),
        present: present
      });
      
      // Find student name for logging
      const student = students.find(s => s.id === studentId);
      const studentName = student ? student.name : 'Student';
      const className = selectedClassInfo ? selectedClassInfo.name : 'Class';
      
      // Log success (removed alert notification as requested by user)
      console.log(`✅ ${studentName} marked as ${present ? 'Present' : 'Absent'} for ${className} on ${selectedDate}`);
      
      fetchData();
      setShowMarkModal(false);
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      // Removed alert notification as requested by user
      console.log('❌ Failed to mark attendance. Please try again.');
    }
  };

  const filteredAttendance = attendance.filter(record => {
    const matchesClass = filterClass === 'all' || record.class_id === filterClass;
    const matchesDate = !filterDate || new Date(record.class_date).toDateString() === new Date(filterDate).toDateString();
    return matchesClass && matchesDate;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-8 gap-3">
          <div className="flex items-start md:items-center">
            {onBack && (
              <button
                onClick={onBack}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 p-2 rounded-lg mr-3 md:mr-4 flex items-center space-x-2 shrink-0"
                data-testid="attendance-back-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden md:inline">Back to Student Center</span>
              </button>
            )}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Attendance</h1>
              <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">
                {user.is_instructor ? 'Manage student attendance' : 'View your attendance history'}
              </p>
              {/* Debug info - will remove after testing */}
              <div className="hidden md:block text-xs text-gray-400 mt-1">
                User: {user.name} | Instructor: {user.is_instructor ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
          
          {user.is_instructor && (
            <div className="flex flex-col items-stretch md:items-end space-y-2 w-full md:w-auto">
              <button
                onClick={() => setShowMarkModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 md:px-6 md:py-3 rounded-lg flex items-center justify-center space-x-2 shadow-lg border-2 border-purple-500 text-sm md:text-base"
                data-testid="attendance-mark-btn"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="font-medium">Mark Attendance</span>
              </button>
              <p className="hidden md:block text-xs text-gray-500">Click to mark student attendance</p>
            </div>
          )}
          
          {!user.is_instructor && (
            <div className="text-xs md:text-sm text-gray-500 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              📝 Only instructors can mark attendance
            </div>
          )}
        </div>

        {/* Quick Actions for Instructors */}
        {user.is_instructor && classes.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
            <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">📋 Quick Actions - Today's Classes</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {classes.slice(0, 3).map((classInfo, index) => (
                <button
                  key={classInfo.id}
                  onClick={() => {
                    setSelectedDate(new Date().toISOString().split('T')[0]);
                    setSelectedClass(classInfo.id);
                    setShowMarkModal(true);
                  }}
                  className={`${
                    index === 0 ? 'bg-blue-600 hover:bg-blue-700' :
                    index === 1 ? 'bg-indigo-600 hover:bg-indigo-700' :
                    'bg-purple-600 hover:bg-purple-700'
                  } text-white p-3 md:p-4 rounded-lg text-center`}
                  data-testid={`quick-mark-class-${classInfo.id}`}
                >
                  <div className="text-xl md:text-2xl mb-1 md:mb-2">🥋</div>
                  <div className="font-medium text-sm md:text-base">{classInfo.name}</div>
                  <div className="text-xs md:text-sm opacity-80">{classInfo.day} at {classInfo.time}</div>
                  <div className="hidden md:block text-xs opacity-60 mt-1">Mark attendance</div>
                </button>
              ))}
            </div>
            {classes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-4">📝</div>
                <p>No classes found for your dojo.</p>
                <p className="text-sm mt-2">Create classes first to mark attendance.</p>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Classes</option>
              {classes.map((classInfo) => (
                <option key={classInfo.id} value={classInfo.id}>
                  {classInfo.name} ({classInfo.day} at {classInfo.time})
                </option>
              ))}
            </select>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              onClick={() => {
                setFilterClass('all');
                setFilterDate('');
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Attendance Records */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {user.is_instructor && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Marked By
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAttendance.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{record.student_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {record.class_name || 'Unknown Class'}
                        {record.class_time && (
                          <div className="text-xs text-gray-400">{record.class_time}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {new Date(record.class_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        record.present 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {record.present ? 'Present' : 'Absent'}
                      </span>
                    </td>
                    {user.is_instructor && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        Instructor
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAttendance.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-gray-500">No attendance records found</p>
            </div>
          )}
        </div>
      </div>

      {/* Mark Attendance Modal */}
      {showMarkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Mark Attendance</h3>
                <button
                  onClick={() => setShowMarkModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {classes.map((classInfo) => (
                      <option key={classInfo.id} value={classInfo.id}>
                        {classInfo.name} ({classInfo.day} at {classInfo.time})
                      </option>
                    ))}
                  </select>
                  {classes.length === 0 && (
                    <p className="text-sm text-red-500 mt-1">
                      No classes available. Create classes first to mark attendance.
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {(() => {
                  // Filter students to show only those enrolled in the selected class
                  const enrolledStudents = students.filter(student => {
                    const studentClasses = student.enrolled_classes || [];
                    return selectedClass && studentClasses.includes(selectedClass);
                  });

                  if (students.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No students found. Please add students first.</p>
                      </div>
                    );
                  }

                  if (!selectedClass) {
                    return (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Please select a class to see enrolled students.</p>
                      </div>
                    );
                  }

                  if (enrolledStudents.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-4">👥</div>
                        <p className="text-gray-500">No students enrolled in this class.</p>
                        <p className="text-sm text-gray-400 mt-2">
                          Students need to be enrolled in this class first to mark attendance.
                        </p>
                      </div>
                    );
                  }

                  return enrolledStudents.map((student) => (
                    <div key={student.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{student.name}</p>
                        <p className="text-sm text-gray-600">{student.email}</p>
                        <p className="text-xs text-gray-500">Belt: {student.belt_level}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => markAttendance(student.id, true)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
                        >
                          ✓ Present
                        </button>
                        <button
                          onClick={() => markAttendance(student.id, false)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium"
                        >
                          ✗ Absent
                        </button>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <NotificationPanel />
    </div>
  );
};

// Payment Center Component (formerly Payment Tracking)
const PaymentCenter = () => {
  const [allStudents, setAllStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingPayment, setUpdatingPayment] = useState({});
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-11 (Jan-Dec)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [paymentStats, setPaymentStats] = useState(null);
  const [loadingPaymentStats, setLoadingPaymentStats] = useState(true);
  const [showLateFeeModal, setShowLateFeeModal] = useState(false);
  const [selectedLateFeeClasses, setSelectedLateFeeClasses] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [whatsappResults, setWhatsappResults] = useState({});
  const [sendingSMS, setSendingSMS] = useState({ late_fee: false });
  const [allClasses, setAllClasses] = useState([]);
  const [groupByClass, setGroupByClass] = useState(false);
  const [expandedClasses, setExpandedClasses] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchAllStudents();
    fetchAllClasses();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    // Calculate payment statistics from backend for selected month
    const fetchPaymentStatisticsForMonth = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/payment-statistics?month=${selectedMonth}&year=${selectedYear}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Use backend data for the selected month
        const stats = response.data;
        setPaymentStats(stats);
        
      } catch (error) {
        console.error('Failed to fetch payment statistics:', error);
        // Fallback to local calculation
        const totalStudents = allStudents.length;
        const paidStudents = allStudents.filter(student => student.payment_status === 'paid').length;
        const yetToPayStudents = totalStudents - paidStudents;
        const monthlyRevenue = allStudents
          .filter(student => student.payment_status === 'paid')
          .reduce((sum, student) => sum + (student.monthly_payment_amount || 0), 0);

        setPaymentStats({
          total_monthly_paying_students: totalStudents,
          total_paid_this_month: paidStudents,
          total_yet_to_pay: yetToPayStudents,
          monthly_revenue: monthlyRevenue
        });
      }
      
      setLoadingPaymentStats(false);
    };

    if (allStudents.length > 0 || !loading) {
      fetchPaymentStatisticsForMonth();
    }
  }, [allStudents, loading, selectedMonth, selectedYear]);

  useEffect(() => {
    // Check for filter parameters in URL
    const urlParams = new URLSearchParams(location.search);
    const filterParam = urlParams.get('filter');
    if (filterParam && ['paid', 'unpaid', 'yet_to_pay'].includes(filterParam)) {
      setPaymentStatusFilter(filterParam);
    }
  }, [location]);

  useEffect(() => {
    // Apply filtering whenever allStudents or filter changes
    applyFilter();
  }, [allStudents, paymentStatusFilter]);

  const handlePaymentStatsClick = (filterType = null) => {
    if (filterType) {
      setPaymentStatusFilter(filterType);
    } else {
      setPaymentStatusFilter('all');
    }
  };

  const applyFilter = () => {
    if (paymentStatusFilter === 'all') {
      setFilteredStudents(allStudents);
    } else if (paymentStatusFilter === 'yet_to_pay') {
      // "Yet to pay" includes only unpaid students
      const filtered = allStudents.filter(student => 
        student.payment_status === 'unpaid'
      );
      setFilteredStudents(filtered);
    } else {
      const filtered = allStudents.filter(student => 
        student.payment_status === paymentStatusFilter
      );
      setFilteredStudents(filtered);
    }
  };

  const fetchAllClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAllClasses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      setAllClasses([]);
    }
  };

  const toggleClassExpansion = (classId) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  const groupStudentsByClass = (students) => {
    const grouped = {};
    const unassigned = [];

    students.forEach(student => {
      if (student.enrolled_classes && student.enrolled_classes.length > 0) {
        student.enrolled_classes.forEach(classId => {
          if (!grouped[classId]) {
            grouped[classId] = [];
          }
          grouped[classId].push(student);
        });
      } else {
        unassigned.push(student);
      }
    });

    return { grouped, unassigned };
  };

  const fetchAllStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/students`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Try to get payment records for the selected month to determine actual payment status
      let paymentRecords = [];
      try {
        const paymentRecordsResponse = await axios.get(`${API}/payment-records?month=${selectedMonth}&year=${selectedYear}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        paymentRecords = paymentRecordsResponse.data || [];
      } catch (error) {
        console.log('Payment records not available, using default status');
        paymentRecords = [];
      }
      
      // Create a map of student payment status from payment records
      const paymentStatusMap = {};
      paymentRecords.forEach(record => {
        paymentStatusMap[record.student_id] = {
          payment_status: record.payment_status,
          response_date: record.marked_at || record.updated_at
        };
      });
      
      // Transform student data to include actual payment status from records
      const studentsWithPaymentStatus = response.data
        .filter(student => !student.is_instructor) // Exclude instructors
        .filter(student => !student.on_vacation) // Exclude students on vacation
        .map(student => {
          const paymentInfo = paymentStatusMap[student.id] || {
            payment_status: 'unpaid',
            response_date: null
          };
          
          return {
            student_id: student.id,
            student_name: student.name,
            student_email: student.email,
            phone_number: student.phone_number || '',
            belt_level: student.belt_level,
            is_trial: student.is_trial,
            monthly_payment_amount: student.monthly_payment_amount || 0,
            on_vacation: student.on_vacation || false,
            enrolled_classes: student.enrolled_classes || [],
            payment_status: paymentInfo.payment_status,
            response_date: paymentInfo.response_date,
            payment_month: selectedMonth,
            payment_year: selectedYear
          };
        });
      
      setAllStudents(studentsWithPaymentStatus);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentStatus = async (studentId, status) => {
    setUpdatingPayment(prev => ({ ...prev, [studentId]: true }));

    try {
      // Get the student's monthly payment amount
      const student = allStudents.find(s => s.student_id === studentId);
      if (!student) {
        throw new Error('Student not found');
      }

      // Record the payment status in the backend for revenue tracking
      const token = localStorage.getItem('token');
      const paymentRecord = {
        student_id: studentId,
        monthly_payment_amount: student.monthly_payment_amount || 0,
        payment_month: selectedMonth,
        payment_year: selectedYear,
        payment_status: status
      };

      await axios.post(`${API}/payment-records`, paymentRecord, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Update local state with the current selected month/year
      setAllStudents(prev => 
        prev.map(student => 
          student.student_id === studentId 
            ? { 
                ...student, 
                payment_status: status, 
                response_date: new Date().toISOString(),
                payment_month: selectedMonth,
                payment_year: selectedYear
              }
            : student
        )
      );
      
    } catch (error) {
      console.error('Failed to update payment status:', error);
      alert('Failed to update payment status. Please try again.');
    } finally {
      setUpdatingPayment(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setLoading(true);
    setLoadingPaymentStats(true);
  };

  const handleYearSelect = (direction) => {
    const newYear = direction === 'prev' ? selectedYear - 1 : selectedYear + 1;
    setSelectedYear(newYear);
    setLoading(true);
    setLoadingPaymentStats(true);
  };

  const handleSendLateFee = async () => {
    setSendingSMS(prev => ({ ...prev, late_fee: true }));
    
    try {
      const requestData = {
        message_type: 'late_fee',
        selected_month: selectedMonth,
        selected_year: selectedYear,
        selected_classes: selectedLateFeeClasses.length === 0 ? null : selectedLateFeeClasses, // null means all classes
        custom_message: `Hello {name} 👋

This is a friendly reminder about your late fee for ${months[selectedMonth]} ${selectedYear}. Please send your payment at your earliest convenience.

If you've already paid, please disregard this message.

Thank you! 🥋`
      };

      const response = await axios.post(`${API}/sms/send-bulk-message`, requestData);
      
      const apiSuccess = response.data && response.data.success !== false && (response.data.successful_sends || response.data.success_count || 0) > 0;
      setWhatsappResults(prev => ({ 
        ...prev, 
        late_fee: {
          success: apiSuccess,
          data: response.data,
          error: apiSuccess ? undefined : (response.data?.error || 'No unpaid students found for the selected month, or none have a monthly fee amount > 0.')
        }
      }));
      
      setShowLateFeeModal(false);
      setSelectedLateFeeClasses([]);
      
      // Clear result after 5 seconds
      setTimeout(() => {
        setWhatsappResults(prev => {
          const newResults = { ...prev };
          delete newResults.late_fee;
          return newResults;
        });
      }, 5000);
      
    } catch (error) {
      console.error('Failed to send late fee reminders:', error);
      setWhatsappResults(prev => ({ 
        ...prev, 
        late_fee: {
          success: false,
          error: error.response?.data?.detail || 'Failed to send late fee reminders'
        }
      }));
    } finally {
      setSendingSMS(prev => ({ ...prev, late_fee: false }));
    }
  };

  const handleLateFeeClassToggle = (classId) => {
    setSelectedLateFeeClasses(prev => {
      if (prev.includes(classId)) {
        // Removing a class
        const newSelection = prev.filter(id => id !== classId);
        return newSelection;
      } else {
        // Adding a class - this automatically unchecks "All Students"
        return [...prev, classId];
      }
    });
  };

  const handleResetMonthlyPayments = async () => {
    const confirmReset = window.confirm(
      `Are you sure you want to reset all paying students to unpaid status for ${months[selectedMonth]} ${selectedYear}?\n\nThis action will mark all non-trial students as unpaid for the current month.`
    );
    
    if (!confirmReset) return;
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/reset-monthly-payments`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      alert(
        `Monthly payments reset completed!\n\n` +
        `${response.data.students_reset} out of ${response.data.total_paying_students} paying students were reset to unpaid status.\n\n` +
        `Month/Year: ${response.data.message}`
      );
      
      // Refresh the payment data
      fetchAllStudents();
      
    } catch (error) {
      console.error('Failed to reset monthly payments:', error);
      alert('Failed to reset monthly payments: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch available classes for late fee selection
  useEffect(() => {
    const fetchClasses = async () => {
      if (showLateFeeModal) {
        setLoadingClasses(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/classes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          const formattedClasses = response.data.map(cls => ({
            id: cls.id,
            name: cls.class_name || cls.name || 'Unnamed Class'
          }));
          
          setClassOptions(formattedClasses);
        } catch (error) {
          console.error('Failed to fetch classes:', error);
          setClassOptions([]);
        } finally {
          setLoadingClasses(false);
        }
      }
    };

    fetchClasses();
  }, [showLateFeeModal]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment tracking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        <div className="mb-4 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Payment Center</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">Track and manage student payment status</p>
        </div>

        {/* Month Selection Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-semibold text-gray-900">📅 Select Month</h3>
            <div className="flex items-center space-x-2 md:space-x-4">
              <button
                onClick={() => handleYearSelect('prev')}
                className="p-2 text-gray-600 hover:text-purple-600 transition-colors"
                title="Previous Year"
                data-testid="payment-year-prev-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-base md:text-lg font-semibold text-gray-900 min-w-[64px] md:min-w-[80px] text-center">
                {selectedYear}
              </span>
              <button
                onClick={() => handleYearSelect('next')}
                className="p-2 text-gray-600 hover:text-purple-600 transition-colors"
                title="Next Year"
                data-testid="payment-year-next-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-12 gap-2">
            {months.map((month, index) => (
              <button
                key={index}
                onClick={() => handleMonthSelect(index)}
                className={`px-2 py-2 md:px-3 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                  selectedMonth === index
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`month-btn-${index}`}
              >
                {month}
              </button>
            ))}
          </div>
          
          <div className="mt-3 text-xs md:text-sm text-gray-600">
            Viewing payments for <strong>{months[selectedMonth]} {selectedYear}</strong>
          </div>
        </div>

        {/* Payment Statistics */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg p-4 md:p-6 text-white mb-4 md:mb-6">
          <h2 className="text-base md:text-xl font-semibold mb-3 md:mb-4">💰 Payment Statistics - {months[selectedMonth]} {selectedYear}</h2>
          {loadingPaymentStats ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <span className="ml-3">Loading payment statistics...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <button
                onClick={() => handlePaymentStatsClick()}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 md:p-4 rounded-lg text-center transition-all transform hover:scale-105"
                data-testid="payment-stat-total-btn"
              >
                <div className="text-2xl md:text-3xl font-bold mb-1">
                  {paymentStats?.total_monthly_paying_students || 0}
                </div>
                <div className="font-medium text-xs md:text-sm leading-tight">Total Monthly Paying Students</div>
                <div className="hidden md:block text-xs opacity-80 mt-1">All students enrolled</div>
              </button>
              
              <button
                onClick={() => handlePaymentStatsClick('paid')}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 md:p-4 rounded-lg text-center transition-all transform hover:scale-105"
                data-testid="payment-stat-paid-btn"
              >
                <div className="text-2xl md:text-3xl font-bold mb-1 text-green-200">
                  {paymentStats?.total_paid_this_month || 0}
                </div>
                <div className="font-medium text-xs md:text-sm leading-tight">Total Students Paid This Month</div>
                <div className="hidden md:block text-xs opacity-80 mt-1">Payments received</div>
              </button>
              
              <button
                onClick={() => handlePaymentStatsClick('yet_to_pay')}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 md:p-4 rounded-lg text-center transition-all transform hover:scale-105"
                data-testid="payment-stat-unpaid-btn"
              >
                <div className="text-2xl md:text-3xl font-bold mb-1 text-red-200">
                  {paymentStats?.total_yet_to_pay || 0}
                </div>
                <div className="font-medium text-xs md:text-sm leading-tight">Students Yet To Pay This Month</div>
                <div className="hidden md:block text-xs opacity-80 mt-1">Pending payments</div>
              </button>

              <div className="bg-white bg-opacity-20 p-3 md:p-4 rounded-lg text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1 text-yellow-200">
                  ${Math.ceil(paymentStats?.monthly_revenue || 0)}
                </div>
                <div className="font-medium text-xs md:text-sm leading-tight">Monthly Revenue</div>
                <div className="hidden md:block text-xs opacity-80 mt-1">Total earnings this month</div>
              </div>
            </div>
          )}
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">🔍 Filter by Payment Status</h3>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <button
              onClick={() => setPaymentStatusFilter('all')}
              className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                paymentStatusFilter === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Students
            </button>
            <button
              onClick={() => setPaymentStatusFilter('paid')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                paymentStatusFilter === 'paid'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Paid Only
            </button>
            <button
              onClick={() => setPaymentStatusFilter('yet_to_pay')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                paymentStatusFilter === 'yet_to_pay'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Yet To Pay
            </button>
            <button
              onClick={() => setPaymentStatusFilter('unpaid')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                paymentStatusFilter === 'unpaid'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Unpaid Only
            </button>
          </div>
          
          {/* Monthly Reset Section */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Monthly Payment Reset</h4>
                <p className="text-xs text-gray-500 mt-1">Mark all paying students as unpaid for the current month</p>
              </div>
              <button
                onClick={handleResetMonthlyPayments}
                disabled={loading}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {loading ? 'Processing...' : 'Reset Monthly Payments'}
              </button>
            </div>
          </div>
          
          {/* Class Grouping Toggle */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {paymentStatusFilter === 'all' && `Showing all ${filteredStudents.length} students for ${months[selectedMonth]} ${selectedYear}`}
              {paymentStatusFilter === 'paid' && `Showing ${filteredStudents.length} students who have paid for ${months[selectedMonth]} ${selectedYear}`}
              {paymentStatusFilter === 'yet_to_pay' && `Showing ${filteredStudents.length} students who haven't paid yet for ${months[selectedMonth]} ${selectedYear}`}
              {paymentStatusFilter === 'unpaid' && `Showing ${filteredStudents.length} students marked as unpaid for ${months[selectedMonth]} ${selectedYear}`}
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Group by Class:</span>
              <button
                onClick={() => setGroupByClass(!groupByClass)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  groupByClass
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {groupByClass ? 'List View' : 'Class View'}
              </button>
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {filteredStudents.length === 0 ? (
            <div className="p-8 text-center">
              {paymentStatusFilter === 'all' ? (
                <p className="text-gray-500">No students found for {months[selectedMonth]} {selectedYear}</p>
              ) : (
                <div>
                  <p className="text-gray-500 mb-2">
                    No students found with filter: {
                      paymentStatusFilter === 'yet_to_pay' ? 'Yet To Pay' :
                      paymentStatusFilter.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                    } for {months[selectedMonth]} {selectedYear}
                  </p>
                  <button
                    onClick={() => setPaymentStatusFilter('all')}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    Clear filter to view all students
                  </button>
                </div>
              )}
            </div>
          ) : groupByClass ? (
            /* Class Grouped View */
            <div className="space-y-4">
              {(() => {
                const { grouped, unassigned } = groupStudentsByClass(filteredStudents);
                
                return (
                  <div className="p-4">
                    {/* Render each class group */}
                    {Object.entries(grouped).map(([classId, students]) => {
                      const classInfo = allClasses.find(c => c.id === classId);
                      const className = classInfo?.name || `Class ${classId}`;
                      const isExpanded = expandedClasses[classId];
                      
                      return (
                        <div key={classId} className="border border-gray-200 rounded-lg mb-4">
                          {/* Class Header */}
                          <div 
                            className="bg-blue-50 p-4 cursor-pointer hover:bg-blue-100 transition-colors"
                            onClick={() => toggleClassExpansion(classId)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-1">
                                  <svg 
                                    className={`w-4 h-4 text-blue-600 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <h3 className="text-lg font-semibold text-blue-900">{className}</h3>
                                </div>
                                {classInfo && (
                                  <div className="text-sm text-blue-700">
                                    {classInfo.days?.join(', ')} • {classInfo.start_time} - {classInfo.end_time}
                                  </div>
                                )}
                              </div>
                              <div className="text-sm text-blue-700 font-medium">
                                {students.length} student{students.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                          
                          {/* Students in this class */}
                          {isExpanded && (
                            <div className="p-4 bg-white">
                              <div className="grid gap-3">
                                {students.map((student) => {
                                  const isUpdating = updatingPayment[student.student_id];
                                  
                                  return (
                                    <div key={student.student_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900">{student.student_name}</div>
                                        <div className="text-sm text-gray-500">
                                          ${(student.monthly_payment_amount || 0).toFixed(2)} • 
                                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                            student.is_trial 
                                              ? 'bg-yellow-100 text-yellow-800' 
                                              : 'bg-green-100 text-green-800'
                                          }`}>
                                            {student.is_trial ? 'Trial' : 'Regular'}
                                          </span>
                                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                            student.payment_status === 'paid'
                                              ? 'bg-green-100 text-green-800'
                                              : 'bg-red-100 text-red-800'
                                          }`}>
                                            {student.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2 ml-4">
                                        <button
                                          onClick={() => updatePaymentStatus(student.student_id, 'paid')}
                                          disabled={isUpdating || student.payment_status === 'paid'}
                                          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs"
                                        >
                                          {isUpdating ? 'Updating...' : 'Mark Paid'}
                                        </button>
                                        <button
                                          onClick={() => updatePaymentStatus(student.student_id, 'unpaid')}
                                          disabled={isUpdating || student.payment_status === 'unpaid'}
                                          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs"
                                        >
                                          {isUpdating ? 'Updating...' : 'Mark Unpaid'}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Unassigned Students */}
                    {unassigned.length > 0 && (
                      <div className="border border-gray-200 rounded-lg">
                        <div 
                          className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => toggleClassExpansion('unassigned')}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-1">
                                <svg 
                                  className={`w-4 h-4 text-gray-600 transform transition-transform ${expandedClasses['unassigned'] ? 'rotate-90' : ''}`}
                                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <h3 className="text-lg font-semibold text-gray-700">No Class Assigned</h3>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 font-medium">
                              {unassigned.length} student{unassigned.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                        
                        {expandedClasses['unassigned'] && (
                          <div className="p-4 bg-white">
                            <div className="grid gap-3">
                              {unassigned.map((student) => {
                                const isUpdating = updatingPayment[student.student_id];
                                
                                return (
                                  <div key={student.student_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{student.student_name}</div>
                                      <div className="text-sm text-gray-500">
                                        ${(student.monthly_payment_amount || 0).toFixed(2)} • 
                                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                          student.is_trial 
                                            ? 'bg-yellow-100 text-yellow-800' 
                                            : 'bg-green-100 text-green-800'
                                        }`}>
                                          {student.is_trial ? 'Trial' : 'Regular'}
                                        </span>
                                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                          student.payment_status === 'paid'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {student.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                      <button
                                        onClick={() => updatePaymentStatus(student.student_id, 'paid')}
                                        disabled={isUpdating || student.payment_status === 'paid'}
                                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs"
                                      >
                                        {isUpdating ? 'Updating...' : 'Mark Paid'}
                                      </button>
                                      <button
                                        onClick={() => updatePaymentStatus(student.student_id, 'unpaid')}
                                        disabled={isUpdating || student.payment_status === 'unpaid'}
                                        className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs"
                                      >
                                        {isUpdating ? 'Updating...' : 'Mark Unpaid'}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            /* Regular Table View */
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monthly Fee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => {
                    const isUpdating = updatingPayment[student.student_id];
                    
                    return (
                      <tr key={student.student_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{student.student_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">${(student.monthly_payment_amount || 0).toFixed(2)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            student.is_trial 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {student.is_trial ? 'Trial' : 'Regular'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            student.payment_status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {student.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => updatePaymentStatus(student.student_id, 'paid')}
                              disabled={isUpdating || student.payment_status === 'paid'}
                              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs"
                            >
                              {isUpdating ? 'Updating...' : 'Mark Paid'}
                            </button>
                            <button
                              onClick={() => updatePaymentStatus(student.student_id, 'unpaid')}
                              disabled={isUpdating || student.payment_status === 'unpaid'}
                              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs"
                            >
                              {isUpdating ? 'Updating...' : 'Mark Unpaid'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Late Fee Modal */}
      {showLateFeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">⏰ Send Late Fee Reminder</h3>
            
            <p className="text-gray-600 mb-4">
              Send late fee reminders to students who haven't paid their fees.
            </p>
            
            {/* Select Month and Year for Late Fee */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Month and Year for Late Fee:
              </label>
              <div className="flex space-x-2 mb-4">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {months.map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={2024}>2024</option>
                  <option value={2025}>2025</option>
                  <option value={2026}>2026</option>
                </select>
              </div>
              
              {/* Class Selection - Now under Month/Year section */}
              <div className="border-t border-gray-200 pt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Classes:
                </label>
                
                {/* All Students Checkbox */}
                <div className="mb-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedLateFeeClasses.length === 0}
                      onChange={() => setSelectedLateFeeClasses([])}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900">All Students (All Classes)</span>
                  </label>
                </div>
                
                <div className="border-t border-gray-200 pt-2">
                  <p className="text-xs text-gray-500 mb-2">Or select specific classes:</p>
                  
                  {loadingClasses ? (
                    <div className="text-sm text-gray-500">Loading classes...</div>
                  ) : classOptions.length > 0 ? (
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {classOptions.map(cls => (
                        <label key={cls.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedLateFeeClasses.includes(cls.id)}
                            onChange={() => handleLateFeeClassToggle(cls.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{cls.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No classes available</div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Message Preview */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Preview:
              </label>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                <p><strong>Hello [Student Name] 👋</strong></p>
                <br />
                <p>This is a friendly reminder about your late fee for {months[selectedMonth]} {selectedYear}. Please send your payment at your earliest convenience.</p>
                <br />
                <p>If you've already paid, please disregard this message.</p>
                <br />
                <p><strong>Thank you! 🥋</strong></p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowLateFeeModal(false);
                  setSelectedLateFeeClasses([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSendLateFee}
                disabled={sendingSMS.late_fee || dailyUsage?.limit_reached}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-4 py-2 rounded-md"
              >
                {sendingSMS.late_fee ? 'Sending...' : 'Send Late Fee Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <NotificationPanel />
    </div>
  );
};

// Class Center Component
const ClassCenter = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [showEditClassModal, setShowEditClassModal] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = () => {
    setShowAddClassModal(true);
  };

  const handleEditClass = (classItem) => {
    setEditingClass(classItem);
    setShowEditClassModal(true);
  };

  const handleDeleteClass = async (classId) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/classes/${classId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        fetchClasses(); // Refresh the class list
      } catch (error) {
        console.error('Failed to delete class:', error);
        alert('Failed to delete class. Please try again.');
      }
    }
  };

  const filteredClasses = classes.filter(classItem => {
    const searchLower = searchTerm.toLowerCase();
    const className = (classItem.class_name || '').toLowerCase();
    const description = (classItem.description || '').toLowerCase();
    
    // Handle multiple possible location/day fields for backward compatibility
    const days = classItem.class_days && classItem.class_days.length > 0
      ? classItem.class_days.join(' ').toLowerCase()
      : (classItem.class_day || classItem.location || '').toLowerCase();
    
    return className.includes(searchLower) || 
           description.includes(searchLower) || 
           days.includes(searchLower);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 pb-20 md:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-8 gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Class Center</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">Manage classes and schedules</p>
          </div>
          
          <button
            onClick={handleAddClass}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 md:px-6 md:py-3 rounded-lg flex items-center justify-center space-x-2 shadow-lg text-sm md:text-base w-full md:w-auto"
            data-testid="class-center-add-btn"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Add New Class</span>
          </button>
        </div>

        {/* 🏛️ Class Management Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-8">
          <div className="flex items-center mb-4 md:mb-6">
            <div className="bg-purple-100 rounded-lg p-2 md:p-3 mr-3 md:mr-4">
              <span className="text-xl md:text-2xl">🏛️</span>
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">Class Management</h2>
              <p className="text-xs md:text-base text-gray-600">Overview of all classes with details and management options</p>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4 md:mb-6">
            <input
              type="text"
              placeholder="Search classes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2.5 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
              data-testid="class-search-input"
            />
          </div>

          {/* Classes Grid */}
          {filteredClasses.length === 0 ? (
            <div className="text-center py-8">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <p className="text-gray-500 text-base md:text-lg">
                {searchTerm ? 'No classes found matching your search.' : 'No classes found. Add your first class to get started!'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredClasses.map((classItem) => (
                <div key={classItem._id} className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3 md:mb-4">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="bg-white rounded-full p-2 mr-3 shrink-0">
                        <svg className="w-5 h-5 md:w-6 md:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 truncate">{classItem.class_name}</h3>
                    </div>
                    <div className="flex space-x-2 shrink-0 ml-2">
                      <button
                        onClick={() => handleEditClass(classItem)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit Class"
                        data-testid={`edit-class-${classItem._id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteClass(classItem._id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete Class"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      <span>{classItem.description}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        {classItem.class_start_time && classItem.class_end_time
                          ? `${classItem.class_start_time} - ${classItem.class_end_time}`
                          : classItem.class_time || 'Time not specified'
                        }
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>
                        {classItem.class_days && classItem.class_days.length > 0
                          ? classItem.class_days.map(day => 
                              day.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                            ).join(', ')
                          : classItem.class_day
                            ? classItem.class_day.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                            : classItem.location
                              ? classItem.location.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                              : 'Days not specified'
                        }
                      </span>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-500">
                        Created {new Date(classItem.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex items-center text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                        Active
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Add Class Modal */}
      {showAddClassModal && (
        <AddClassModal
          isOpen={showAddClassModal}
          onClose={() => setShowAddClassModal(false)}
          onSuccess={() => {
            setShowAddClassModal(false);
            fetchClasses();
          }}
        />
      )}
      
      {/* Edit Class Modal */}
      {showEditClassModal && editingClass && (
        <EditClassModal
          isOpen={showEditClassModal}
          classData={editingClass}
          onClose={() => setShowEditClassModal(false)}
          onSuccess={() => {
            setShowEditClassModal(false);
            fetchClasses();
          }}
        />
      )}

      <NotificationPanel />
    </div>
  );
};

// Add Class Modal Component
const AddClassModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    class_name: '',
    description: '',
    class_days: [],         // Changed: Array for multiple days
    class_start_time: '',   // Changed: Separate start time
    class_end_time: ''      // Changed: Separate end time
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Days of the week options
  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Time slot options for start time
  const startTimeSlots = [
    { value: '6:00 AM', label: '6:00 AM' },
    { value: '7:00 AM', label: '7:00 AM' },
    { value: '8:00 AM', label: '8:00 AM' },
    { value: '9:00 AM', label: '9:00 AM' },
    { value: '10:00 AM', label: '10:00 AM' },
    { value: '11:00 AM', label: '11:00 AM' },
    { value: '12:00 PM', label: '12:00 PM' },
    { value: '1:00 PM', label: '1:00 PM' },
    { value: '2:00 PM', label: '2:00 PM' },
    { value: '3:00 PM', label: '3:00 PM' },
    { value: '4:00 PM', label: '4:00 PM' },
    { value: '5:00 PM', label: '5:00 PM' },
    { value: '6:00 PM', label: '6:00 PM' },
    { value: '7:00 PM', label: '7:00 PM' },
    { value: '8:00 PM', label: '8:00 PM' },
    { value: '9:00 PM', label: '9:00 PM' }
  ];

  // Time slot options for end time
  const endTimeSlots = [
    { value: '7:00 AM', label: '7:00 AM' },
    { value: '8:00 AM', label: '8:00 AM' },
    { value: '9:00 AM', label: '9:00 AM' },
    { value: '10:00 AM', label: '10:00 AM' },
    { value: '11:00 AM', label: '11:00 AM' },
    { value: '12:00 PM', label: '12:00 PM' },
    { value: '1:00 PM', label: '1:00 PM' },
    { value: '2:00 PM', label: '2:00 PM' },
    { value: '3:00 PM', label: '3:00 PM' },
    { value: '4:00 PM', label: '4:00 PM' },
    { value: '5:00 PM', label: '5:00 PM' },
    { value: '6:00 PM', label: '6:00 PM' },
    { value: '7:00 PM', label: '7:00 PM' },
    { value: '8:00 PM', label: '8:00 PM' },
    { value: '9:00 PM', label: '9:00 PM' },
    { value: '10:00 PM', label: '10:00 PM' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For class name, only allow alphabetical characters and spaces
    if (name === 'class_name') {
      const alphabeticalValue = value.replace(/[^A-Za-z\s]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: alphabeticalValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle multiple days selection
  const handleDayChange = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      class_days: prev.class_days.includes(dayValue)
        ? prev.class_days.filter(day => day !== dayValue)
        : [...prev.class_days, dayValue]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/classes`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Failed to create class:', error);
      setError(error.response?.data?.detail || 'Failed to create class');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      class_name: '',
      description: '',
      class_days: [],         // Changed: Reset to empty array for multiple days
      class_start_time: '',   // Changed: Reset separate start time
      class_end_time: ''      // Changed: Reset separate end time
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Add New Class</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="class_name"
                value={formData.class_name}
                onChange={handleInputChange}
                placeholder="e.g., Beginner Karate, Advanced Training"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Only alphabetical characters allowed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Days <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {daysOfWeek.map(day => (
                  <label key={day.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.class_days.includes(day.value)}
                      onChange={() => handleDayChange(day.value)}
                      className="text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{day.label}</span>
                  </label>
                ))}
              </div>
              {formData.class_days.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">Select at least one day for the class</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <select
                  name="class_start_time"
                  value={formData.class_start_time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select start time</option>
                  {startTimeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time <span className="text-red-500">*</span>
                </label>
                <select
                  name="class_end_time"
                  value={formData.class_end_time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select end time</option>
                  {endTimeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white px-4 py-2 rounded-lg"
            >
              {loading ? 'Creating...' : 'Create Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Class Modal Component
const EditClassModal = ({ isOpen, classData, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    class_name: classData?.class_name || '',
    description: classData?.description || '',
    class_days: classData?.class_days || [],
    class_start_time: classData?.class_start_time || '',  // Changed: Separate start time
    class_end_time: classData?.class_end_time || ''       // Changed: Separate end time
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Days of the week options
  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Time slot options for start time
  const startTimeSlots = [
    { value: '6:00 AM', label: '6:00 AM' },
    { value: '7:00 AM', label: '7:00 AM' },
    { value: '8:00 AM', label: '8:00 AM' },
    { value: '9:00 AM', label: '9:00 AM' },
    { value: '10:00 AM', label: '10:00 AM' },
    { value: '11:00 AM', label: '11:00 AM' },
    { value: '12:00 PM', label: '12:00 PM' },
    { value: '1:00 PM', label: '1:00 PM' },
    { value: '2:00 PM', label: '2:00 PM' },
    { value: '3:00 PM', label: '3:00 PM' },
    { value: '4:00 PM', label: '4:00 PM' },
    { value: '5:00 PM', label: '5:00 PM' },
    { value: '6:00 PM', label: '6:00 PM' },
    { value: '7:00 PM', label: '7:00 PM' },
    { value: '8:00 PM', label: '8:00 PM' },
    { value: '9:00 PM', label: '9:00 PM' }
  ];

  // Time slot options for end time
  const endTimeSlots = [
    { value: '7:00 AM', label: '7:00 AM' },
    { value: '8:00 AM', label: '8:00 AM' },
    { value: '9:00 AM', label: '9:00 AM' },
    { value: '10:00 AM', label: '10:00 AM' },
    { value: '11:00 AM', label: '11:00 AM' },
    { value: '12:00 PM', label: '12:00 PM' },
    { value: '1:00 PM', label: '1:00 PM' },
    { value: '2:00 PM', label: '2:00 PM' },
    { value: '3:00 PM', label: '3:00 PM' },
    { value: '4:00 PM', label: '4:00 PM' },
    { value: '5:00 PM', label: '5:00 PM' },
    { value: '6:00 PM', label: '6:00 PM' },
    { value: '7:00 PM', label: '7:00 PM' },
    { value: '8:00 PM', label: '8:00 PM' },
    { value: '9:00 PM', label: '9:00 PM' },
    { value: '10:00 PM', label: '10:00 PM' }
  ];

  // Update form data when classData changes
  useEffect(() => {
    if (classData) {
      setFormData({
        class_name: classData.class_name || '',
        description: classData.description || '',
        class_days: classData.class_days || [],
        class_start_time: classData.class_start_time || '',  // Changed: Separate start time
        class_end_time: classData.class_end_time || ''       // Changed: Separate end time
      });
    }
  }, [classData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For class name, only allow alphabetical characters and spaces
    if (name === 'class_name') {
      const alphabeticalValue = value.replace(/[^A-Za-z\s]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: alphabeticalValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle multiple days selection
  const handleDayChange = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      class_days: prev.class_days.includes(dayValue)
        ? prev.class_days.filter(day => day !== dayValue)
        : [...prev.class_days, dayValue]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/classes/${classData._id}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Failed to update class:', error);
      setError(error.response?.data?.detail || 'Failed to update class');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this class? This action cannot be undone.')) {
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API}/classes/${classData._id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 200) {
          onSuccess();
          handleClose();
        }
      } catch (error) {
        console.error('Failed to delete class:', error);
        setError(error.response?.data?.detail || 'Failed to delete class');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Edit Class</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="class_name"
                value={formData.class_name}
                onChange={handleInputChange}
                placeholder="e.g., Beginner Karate, Advanced Training"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Only alphabetical characters allowed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Days <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {daysOfWeek.map(day => (
                  <label key={day.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.class_days.includes(day.value)}
                      onChange={() => handleDayChange(day.value)}
                      className="text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{day.label}</span>
                  </label>
                ))}
              </div>
              {formData.class_days.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">Select at least one day for the class</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <select
                  name="class_start_time"
                  value={formData.class_start_time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select start time</option>
                  {startTimeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time <span className="text-red-500">*</span>
                </label>
                <select
                  name="class_end_time"
                  value={formData.class_end_time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select end time</option>
                  {endTimeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-4 py-2 rounded-lg"
            >
              {loading ? 'Deleting...' : 'Delete Class'}
            </button>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white px-4 py-2 rounded-lg"
              >
                {loading ? 'Updating...' : 'Update Class'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Home Page Component (Public)
const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSenseiSetupModal, setShowSenseiSetupModal] = useState(false);

  // If user is already logged in, redirect to appropriate dashboard
  useEffect(() => {
    if (user) {
      if (user.is_super_admin) {
        navigate('/super-admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  const handleGetStarted = () => {
    setShowLoginModal(true);
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleOpenSenseiSetup = () => {
    setShowLoginModal(false); // Close login modal
    setShowSenseiSetupModal(true); // Open setup modal
  };

  const handleCloseSenseiSetup = () => {
    setShowSenseiSetupModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-3">
              <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg md:text-xl">🥋</span>
              </div>
              <h1 className="text-lg md:text-2xl font-bold text-gray-900">Sensei Suite</h1>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              <button
                onClick={() => navigate('/consent-form')}
                className="text-purple-600 hover:text-purple-800 font-medium transition-colors text-sm md:text-base"
                data-testid="landing-consent-form-btn"
              >
                Consent Form
              </button>
              <button
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2 md:px-6 md:py-2 rounded-lg font-medium transition-all text-sm md:text-base"
                data-testid="landing-login-btn"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            Sensei Suite
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Streamline your martial arts dojo operations with our comprehensive management platform. 
            Handle student enrollment, payments, communications, and class management all in one place.
          </p>
        </div>

        {/* Ready to Streamline Your Dojo Section - Moved Above Features */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 md:p-12 text-center text-white mb-8 md:mb-16">
          <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Ready to Streamline Your Dojo?</h3>
          <p className="text-base md:text-xl mb-6 md:mb-8 opacity-90">
            Join hundreds of martial arts instructors who trust our platform to manage their dojos efficiently.
          </p>
          <button
            onClick={handleGetStarted}
            className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-6 py-2.5 md:px-8 md:py-3 rounded-lg text-base md:text-lg transition-colors"
            data-testid="landing-get-started-btn"
          >
            Get Started Today
          </button>
        </div>

        {/* Features Grid — mobile 2×3, tablet 2×3, desktop 3×2 */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8 mb-8 md:mb-16">
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <span className="text-white text-xl md:text-2xl">👥</span>
            </div>
            <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">Student Management</h3>
            <p className="text-xs md:text-base text-gray-600">
              Easily enroll, manage, and track student progress. Handle class preferences, 
              contact information, and individual monthly payment amounts.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <span className="text-white text-xl md:text-2xl">💰</span>
            </div>
            <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">Payment Tracking</h3>
            <p className="text-xs md:text-base text-gray-600">
              Monitor monthly fees, track payment status, and view detailed revenue analytics. 
              Get instant insights into your dojo's financial health.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <span className="text-white text-xl md:text-2xl">📱</span>
            </div>
            <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">SMS Integration</h3>
            <p className="text-xs md:text-base text-gray-600">
              Send announcements, payment reminders, and class updates directly through SMS messaging. 
              Keep your students informed effortlessly.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <span className="text-white text-xl md:text-2xl">🏛️</span>
            </div>
            <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">Class Management</h3>
            <p className="text-xs md:text-base text-gray-600">
              Create and manage multiple classes with detailed schedules, descriptions, 
              and locations. Track student enrollment for each class.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <span className="text-white text-xl md:text-2xl">📊</span>
            </div>
            <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">Analytics Dashboard</h3>
            <p className="text-xs md:text-base text-gray-600">
              View comprehensive statistics including student demographics, payment trends, 
              birthday calendar, and performance metrics.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
              <span className="text-white text-xl md:text-2xl">📢</span>
            </div>
            <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">Message Center</h3>
            <p className="text-xs md:text-base text-gray-600">
              Send targeted announcements to specific classes or all students. 
              Use pre-defined templates or create custom messages.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}

      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={handleCloseModal} 
        onOpenSenseiSetup={handleOpenSenseiSetup}
      />
      
      {/* Sensei Setup Modal */}
      <SenseiSetupModal 
        isOpen={showSenseiSetupModal} 
        onClose={handleCloseSenseiSetup}
      />
    </div>
  );
};

// App Content Component - handles loading state
const AppContent = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/consent-form" element={<ConsentForm />} />
          <Route path="/login" element={<Login />} />
          <Route path="/super-admin" element={
            <ProtectedRoute>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/desktop/*" element={
            <ProtectedRoute>
              <div>
                <VersionSwitcher />
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/students" element={<ManageStudents />} />
                  <Route path="/attendance" element={<AttendanceManagement />} />
                  <Route path="/class-center" element={<ClassCenter />} />
                  <Route path="/announcements" element={<AnnouncementsManagement />} />
                  <Route path="/payment-center" element={<PaymentCenter />} />
                  <Route path="/payment-tracking" element={<PaymentCenter />} />
                  <Route path="/sms-setup" element={<SMSSetup />} />
                  <Route path="/notification-settings" element={<NotificationSettings />} />
                </Routes>
              </div>
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/students" element={
            <ProtectedRoute>
              <ManageStudents />
            </ProtectedRoute>
          } />
          <Route path="/attendance" element={
            <ProtectedRoute>
              <AttendanceManagement />
            </ProtectedRoute>
          } />
          <Route path="/class-center" element={
            <ProtectedRoute>
              <ClassCenter />
            </ProtectedRoute>
          } />
          <Route path="/message-center" element={
            <ProtectedRoute>
              <AnnouncementsManagement />
            </ProtectedRoute>
          } />
          <Route path="/announcements" element={
            <ProtectedRoute>
              <AnnouncementsManagement />
            </ProtectedRoute>
          } />
          <Route path="/payment-center" element={
            <ProtectedRoute>
              <PaymentCenter />
            </ProtectedRoute>
          } />
          <Route path="/payment-tracking" element={
            <ProtectedRoute>
              <PaymentCenter />
            </ProtectedRoute>
          } />
          <Route path="/sms-setup" element={
            <ProtectedRoute>
              <SMSSetup />
            </ProtectedRoute>
          } />
          <Route path="/notification-settings" element={
            <ProtectedRoute>
              <NotificationSettings />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
      
      {/* Global Footer - appears on all pages at the very bottom */}
      <footer className="w-full bg-black text-white py-6 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg text-white font-bold">
            Sensei Suite: Mastering Martial Arts, Powered by NASCENTAA.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Consent Form Component
const ConsentForm = () => {
  const [formData, setFormData] = useState({
    sensei_name: '',
    phone_number: '',
    email: '',
    signature: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.sensei_name.trim()) {
      setError('Sensei name is required');
      setLoading(false);
      return;
    }

    if (!formData.phone_number.trim()) {
      setError('Phone number is required');
      setLoading(false);
      return;
    }

    if (!formData.signature.trim()) {
      setError('Digital signature is required');
      setLoading(false);
      return;
    }

    try {
      const submissionData = {
        ...formData,
        date: new Date().toISOString().split('T')[0] // Current date in YYYY-MM-DD format
      };

      const response = await axios.post(`${API}/consent-form`, submissionData);

      if (response.status === 200) {
        setSuccess(true);
        setFormData({
          sensei_name: '',
          phone_number: '',
          email: '',
          signature: ''
        });
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit consent form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-purple-200 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="mb-4 text-purple-600 hover:text-purple-800 transition-colors"
          >
            ← Back to Home
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Text Message Consent</h1>
          <p className="text-gray-600">Sensei Suite - SMS Communication Agreement</p>
        </div>

        {success ? (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-2xl font-bold text-green-600 mb-4">Consent Submitted Successfully!</h2>
            <p className="text-gray-600 mb-6">
              Your text messaging consent agreement has been submitted and admin@senseisuite.com has been notified.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Return to Home
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Consent Agreement */}
            <section className="mb-8">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  I agree to receive text messages from Sensei Suite at the phone number I have provided. I understand that these messages may include:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Updates about my account or appointments</li>
                  <li>Reminders about upcoming events or services</li>
                  <li>Announcements about new features</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  I understand that message and data rates may apply. I can reply email Stop to senseisuite.com at any time to opt out of receiving messages.
                </p>
              </div>
            </section>

            {/* Form Fields */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sensei Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="sensei_name"
                    value={formData.sensei_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Digital Signature <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="signature"
                  value={formData.signature}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-cursive"
                  placeholder="Type your full name as your digital signature"
                  style={{ fontFamily: 'cursive' }}
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  By typing your name here, you are providing your digital signature for this agreement.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Date:</strong> {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Submitting...' : 'Submit Agreement'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;